/**
 * template/sections/education/EducationExamPagesSection.jsx
 *
 * AVPS Academy Exam Portal inner pages — READER (published page) side.
 * Covers the three inner pages of the shared design bundle:
 *   `educationExamCourses`      → courses.html        (catalog + filters + categories)
 *   `educationExamCourseDetail` → course-details.html (overview, curriculum, side rail)
 *   `educationExamAbout`        → about.html          (story, purpose, approach, values)
 *
 * Renders IDENTICALLY to the Console builder preview version
 * (admin-console-ui/src/modules/template-builder/sections/EducationExamPagesSection.jsx).
 *
 * STYLING NOTE: the shared bundle styles these three pages from an external
 * `academy-pages.css`, which was not supplied with the HTML. The markup and copy below
 * follow the supplied HTML exactly; the presentation is built from the design tokens and
 * component treatments already established by the home page (`index.html`, converted in
 * EducationExamHomeSection.jsx) so all four pages remain one coherent system.
 *
 * CHROME: these pages render the design's own nav and footer, reused directly from the
 * home section so they cannot drift. Their section types are therefore registered in
 * template/selfChromedSections.js, which suppresses the shell + template chrome.
 */

import React from 'react';
import { createInquiry } from '../../../api/action/inquiry.jsx';
import FormatByOperatingLocation from '../../../components/FormatByOperatingLocation.jsx';
import { CalendarDays, Check, Clock3, MonitorPlay, Sparkles, Video } from 'lucide-react';
import { getEmailValidationMessage } from '../../../utils/contactUtils.js';
import { getEffectiveDurationInMinutes } from '../../../utils/durationUtils.js';
import { buildInquiryNotificationFields } from '../../TemplateContactUtils.jsx';
import { getTemplateCompanyContext } from '../../TemplateCompanyUtils.js';

import {
  DEFAULTS as CHROME_DEFAULTS,
  DEFAULT_STUDENT_IMAGE,
  EXAM_HOME_CSS,
  ExamFooter,
  ExamNav,
  examLinkProps,
  getExamCompanyName,
  getExamChromeContent,
  plainText,
  visibleItems
} from './EducationExamHomeSection.jsx';

export const EXAM_PAGES_TYPES = Object.freeze({
  COURSES: 'educationExamCourses',
  COURSE_DETAIL: 'educationExamCourseDetail',
  ABOUT: 'educationExamAbout',
  CONTACT: 'educationExamContact',
  INFO: 'educationExamInfo',
  /**
   * Frame-only variant for the FUNCTIONAL pages (cart, order history, sign-in, sign-up).
   * It renders the design's nav, page head and footer, and hosts the existing live
   * component as `children` so checkout and auth keep working untouched.
   */
  SHELL: 'educationExamShell'
});

const EXAM_PAGES_CSS = `
.aae-home .aae-page-hero{position:relative;color:#fff;background:radial-gradient(circle at 70% 15%,rgba(255,255,255,.10),transparent 28%),linear-gradient(126deg,#217967 0%,#258d79 46%,#2a9a85 100%);padding-bottom:72px;overflow:hidden;isolation:isolate}
.aae-home .aae-course-detail-loader{min-height:100vh;padding:24px 0 80px;background:#fff}
.aae-home .aae-course-detail-skeleton-nav,.aae-home .aae-course-detail-skeleton-content{width:min(1240px,calc(100% - 80px));margin:0 auto}
.aae-home .aae-course-detail-skeleton-nav{height:74px;display:grid;grid-template-columns:1fr 240px 1fr;align-items:center;gap:28px}
.aae-home .aae-course-detail-skeleton-nav>*:last-child{justify-self:end}
.aae-home .aae-course-detail-skeleton-content{padding-top:58px}
.aae-home .aae-course-detail-skeleton-crumb{width:180px;height:12px;margin-bottom:34px}
.aae-home .aae-course-detail-skeleton-eyebrow{width:170px;height:28px;margin-bottom:22px;border-radius:999px}
.aae-home .aae-course-detail-skeleton-title{width:min(720px,80%);height:58px;margin-bottom:18px;border-radius:12px}
.aae-home .aae-course-detail-skeleton-subtitle{width:min(620px,75%);height:18px;margin-bottom:10px;border-radius:8px}
.aae-home .aae-course-detail-skeleton-subtitle.short{width:min(460px,58%);margin-bottom:52px}
.aae-home .aae-course-detail-skeleton-grid{display:grid;grid-template-columns:minmax(0,1fr) 360px;gap:28px}
.aae-home .aae-course-detail-skeleton-panel{min-height:250px;padding:32px;border:1px solid #ededed;border-radius:26px}
.aae-home .aae-course-detail-skeleton-panel.side{min-height:330px}
.aae-home .aae-course-detail-skeleton-line{height:16px;margin-bottom:16px;border-radius:8px}
.aae-home .aae-course-detail-skeleton-line.heading{width:45%;height:26px;margin-bottom:28px}
.aae-home .aae-course-detail-skeleton-line.wide{width:92%}
.aae-home .aae-course-detail-skeleton-line.medium{width:72%}
.aae-home .aae-course-detail-skeleton-line.narrow{width:54%}
.aae-home .aae-course-detail-skeleton-line.price{width:36%;height:36px;margin:28px 0}
.aae-home .aae-course-detail-skeleton-line.button{width:100%;height:52px;margin-top:30px;border-radius:999px}
.aae-home .aae-course-detail-skeleton-block{height:86px;margin-top:30px;border-radius:18px}
.aae-home .aae-course-detail-skeleton-nav>* ,.aae-home .aae-course-detail-skeleton-crumb,.aae-home .aae-course-detail-skeleton-eyebrow,.aae-home .aae-course-detail-skeleton-title,.aae-home .aae-course-detail-skeleton-subtitle,.aae-home .aae-course-detail-skeleton-line,.aae-home .aae-course-detail-skeleton-block{background:linear-gradient(90deg,#f3f3f3 25%,#e7e7e7 37%,#f3f3f3 63%);background-size:400% 100%;animation:aae-course-detail-skeleton-shimmer 1.4s ease infinite}
@keyframes aae-course-detail-skeleton-shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}
.aae-home .aae-page-hero h1,.aae-home .aae-page-hero h2,.aae-home .aae-page-hero p{color:#fff}
.aae-home .aae-page-hero.aae-auth-page-hero{padding-bottom:14px}
.aae-home .aae-page-hero.aae-auth-page-hero .aae-nav{padding-top:14px;padding-bottom:6px}
.aae-home .aae-page-hero.aae-auth-page-hero .aae-page-head{padding-top:16px}
.aae-home .aae-page-hero.aae-auth-page-hero .aae-crumb{margin-bottom:8px}
.aae-home .aae-page-hero.aae-auth-page-hero .aae-page-head p{margin-top:8px}
.aae-home .aae-page-hero.aae-auth-page-hero + .aae-shell-body{padding-top:20px}
/* Wave offsets are retuned for the shorter page heroes — the landing values (208/290/396/500)
   are spaced for its 715px hero and would spill past a page hero's lower edge. */
.aae-home .aae-page-hero .aae-wave.w1{top:150px}
.aae-home .aae-page-hero .aae-wave.w2{top:228px}
.aae-home .aae-page-hero .aae-wave.w3{top:306px}
.aae-home .aae-page-hero .aae-wave.w4{top:384px}
.aae-home .aae-page-head{width:min(1240px,calc(100% - 80px));margin:0 auto;padding:56px 0 0}
.aae-home .aae-crumb{display:flex;align-items:center;gap:10px;font-size:13px;font-weight:700;color:rgba(255,255,255,.72);margin-bottom:18px}
.aae-home .aae-crumb strong{color:#fff}
.aae-home .aae-page-head h1{max-width:820px;font-size:clamp(38px,4.6vw,60px);line-height:1.02;letter-spacing:-2.4px}
.aae-home .aae-page-head p{max-width:680px;margin:18px 0 0;color:rgba(255,255,255,.82);font-size:17px;line-height:1.65}
.aae-home .aae-hero-tools{display:flex;align-items:center;gap:22px;flex-wrap:wrap;margin-top:30px}
.aae-home .aae-searchbox{display:flex;align-items:center;gap:12px;background:#fff;border-radius:999px;padding:8px 8px 8px 20px;box-shadow:0 18px 40px rgba(12,54,49,.22);min-width:min(560px,100%)}
.aae-home .aae-searchbox>span{color:#5e6864;font-weight:900}
.aae-home .aae-searchbox input{flex:1;border:0;outline:0;font:inherit;font-size:14px;color:var(--aae-ink);background:transparent;min-width:80px}
.aae-home .aae-searchbox button{border:0;cursor:pointer;background:var(--aae-orange);color:#151515;font-weight:900;font-size:14px;border-radius:999px;padding:13px 22px}
.aae-home .aae-hero-count{color:#fff}
.aae-home .aae-hero-count strong{display:block;font-size:26px;letter-spacing:-1px}
.aae-home .aae-hero-count span{color:rgba(255,255,255,.7);font-size:13px}

.aae-home .aae-section{padding:88px 0}
.aae-home .aae-catalog{display:grid;grid-template-columns:290px 1fr;gap:28px;margin-top:36px;align-items:start}
.aae-home .aae-catalog .aae-course-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:24px}
.aae-home .aae-course-art .aae-product-image{position:absolute;inset:0;z-index:1;width:100%;height:100%;display:block;object-fit:cover;background:#fff}
.aae-home .aae-filters{background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:28px;padding:26px;box-shadow:0 18px 45px rgba(45,56,52,.07);position:sticky;top:24px}
.aae-home .aae-filter-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:20px}
.aae-home .aae-filter-head h3{font-size:19px;letter-spacing:-.5px}
.aae-home .aae-reset{border:0;background:transparent;cursor:pointer;color:var(--aae-teal-700);font-weight:800;font-size:12px}
.aae-home .aae-filter-group{padding:18px 0;border-top:1px solid rgba(29,61,55,.09)}
.aae-home .aae-filter-group h4{margin:0 0 12px;font-size:12px;text-transform:uppercase;letter-spacing:1.2px;color:#5e6864}
.aae-home .aae-check{display:flex;align-items:center;gap:10px;margin:0 0 10px;font-size:14px;color:#3f4a47;cursor:pointer}
.aae-home .aae-check input{width:17px;height:17px;accent-color:var(--aae-teal-700)}
.aae-home .aae-results-head{display:flex;align-items:center;justify-content:space-between;gap:18px;flex-wrap:wrap;margin-bottom:20px}
.aae-home .aae-results-head strong{font-size:15px}
.aae-home .aae-results-head select{border:1px solid rgba(31,90,84,.16);border-radius:999px;padding:11px 16px;font:inherit;font-size:13px;font-weight:700;background:#fff;color:var(--aae-ink)}
.aae-home .aae-filters .aae-field input{width:100%}
.aae-home .aae-empty{background:#fff;border:1px dashed rgba(31,90,84,.24);border-radius:26px;padding:44px 28px;text-align:center}
.aae-home .aae-empty strong{display:block;font-size:18px;margin-bottom:6px}
.aae-home .aae-empty span{color:#68706d;font-size:14px}

.aae-home .aae-categories{padding:92px 0;background:var(--aae-lime-soft)}
.aae-home .aae-category-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:16px;margin-top:34px}
.aae-home .aae-category{display:flex;width:100%;align-items:center;gap:14px;padding:20px;border-radius:22px;background:rgba(255,255,255,.75);border:1px solid rgba(31,90,84,.12);box-shadow:0 14px 32px rgba(41,73,64,.07);font:inherit;text-align:left;cursor:pointer;color:var(--aae-ink)}
.aae-home .aae-category i{width:46px;height:46px;flex:0 0 46px;display:grid;place-items:center;border-radius:14px;background:var(--aae-teal-950);color:var(--aae-lime);font-style:normal;font-weight:900;font-size:15px}
.aae-home .aae-category strong{display:block;font-size:15px}
.aae-home .aae-category span{color:#66716e;font-size:12px}

.aae-home .aae-detail-grid{display:grid;grid-template-columns:1.35fr .65fr;gap:40px;align-items:start;margin-top:28px}
.aae-home .aae-detail-pills{display:flex;gap:9px;flex-wrap:wrap;margin:24px 0 0}
.aae-home .aae-detail-pills span{background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.24);border-radius:999px;padding:9px 14px;font-size:12px;font-weight:800}
.aae-home .aae-instructor{display:flex;align-items:center;gap:14px;margin-top:26px}
.aae-home .aae-avatar{width:52px;height:52px;flex:0 0 52px;display:grid;place-items:center;border-radius:50%;background:var(--aae-lime);color:#15302c;font-weight:900}
.aae-home .aae-instructor strong{display:block;font-size:16px}
.aae-home .aae-instructor span{color:rgba(255,255,255,.7);font-size:13px}
.aae-home .aae-preview-card{background:#fff;color:var(--aae-ink);border-radius:30px;padding:22px;box-shadow:0 30px 70px rgba(11,52,47,.3)}
.aae-home .aae-preview-art{position:relative;height:180px;border-radius:22px;display:grid;place-items:center;background:linear-gradient(135deg,#bfd8ff,#d8c8ff);margin-bottom:20px}
.aae-home .aae-play{width:64px;height:64px;display:grid;place-items:center;border-radius:50%;background:rgba(255,255,255,.85);color:var(--aae-teal-900);font-size:22px;font-weight:900}
.aae-home .aae-preview-card h3{font-size:20px;letter-spacing:-.6px}
.aae-home .aae-price{margin:10px 0 18px;font-size:34px;font-weight:900;letter-spacing:-1.6px}
.aae-home .aae-interview-picker{display:grid;gap:14px;margin-top:18px}
.aae-home .aae-booking-step{display:grid;gap:8px}
.aae-home .aae-booking-step-label{display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:1px;color:#69736f}
.aae-home .aae-booking-step-label strong{color:var(--aae-ink);font-size:13px;letter-spacing:0;text-transform:none}
.aae-home .aae-date-nav{display:grid;grid-template-columns:36px 1fr 36px;gap:6px;align-items:center}
.aae-home .aae-date-nav button{display:grid;place-items:center;width:36px;height:36px;border:1px solid rgba(31,90,84,.16);border-radius:12px;background:#fff;color:var(--aae-teal-950);font-size:18px;cursor:pointer}
.aae-home .aae-date-nav button:disabled{opacity:.35;cursor:not-allowed}
.aae-home .aae-date-nav input{min-width:0;text-align:center;padding:10px 8px!important;border-radius:12px!important;font-weight:800!important}
.aae-home .aae-date-caption{text-align:center;color:#69736f;font-size:12px;margin-top:-2px}
.aae-home .aae-interviewer-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.aae-home .aae-interviewer-option{display:grid;grid-template-columns:30px minmax(0,1fr);align-items:center;gap:8px;padding:9px;text-align:left;border:1px solid rgba(31,90,84,.14);border-radius:14px;background:#fff;color:var(--aae-ink);cursor:pointer}
.aae-home .aae-interviewer-option.is-selected{border-color:var(--aae-teal-700);background:#edf8f3;box-shadow:inset 0 0 0 1px var(--aae-teal-700)}
.aae-home .aae-interviewer-avatar{display:grid;place-items:center;width:30px;height:30px;border-radius:50%;background:var(--aae-lime);color:#15302c;font-size:11px;font-weight:900}
.aae-home .aae-interviewer-option strong{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px}
.aae-home .aae-interviewer-option span span{display:block;color:#6b7772;font-size:10px;margin-top:2px}
.aae-home .aae-interviewer-copy{min-width:0}
.aae-home .aae-interviewer-skills{display:flex!important;flex-wrap:wrap;gap:4px;margin-top:5px!important}
.aae-home .aae-interviewer-skills em{font-style:normal;white-space:nowrap;padding:3px 6px;border-radius:6px;background:#e9f6ef;color:#19634f;font-size:9px;font-weight:800}
.aae-home .aae-interviewer-skills-empty{display:block;color:#8a9690;font-size:9px;margin-top:5px}
.aae-home .aae-interviewer-skill-filter{display:flex;flex-wrap:wrap;gap:6px;margin:2px 0 4px}
.aae-home .aae-interviewer-skill-filter button{border:1px solid rgba(31,90,84,.16);border-radius:999px;padding:7px 10px;background:#f8fbf9;color:#35645b;font:inherit;font-size:10px;font-weight:800;cursor:pointer}
.aae-home .aae-interviewer-skill-filter button.is-selected{border-color:var(--aae-teal-700);background:#dff4e9;color:#125448}
.aae-home .aae-period-tabs{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
.aae-home .aae-period-tab{border:1px solid rgba(31,90,84,.14);border-radius:11px;padding:8px 4px;background:#fff;color:#52615c;font:inherit;font-size:11px;font-weight:800;cursor:pointer}
.aae-home .aae-period-tab.is-selected{background:var(--aae-teal-950);border-color:var(--aae-teal-950);color:#fff}
.aae-home .aae-time-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px}
.aae-home .aae-time-option{border:1px solid rgba(31,90,84,.16);border-radius:11px;padding:10px 4px;background:#fff;color:var(--aae-teal-950);font:inherit;font-size:12px;font-weight:800;cursor:pointer}
.aae-home .aae-time-option:hover{border-color:var(--aae-teal-700);background:#edf8f3}
.aae-home .aae-time-option.is-selected{background:var(--aae-teal-700);border-color:var(--aae-teal-700);color:#fff}
.aae-home .aae-no-times{padding:12px;border-radius:12px;background:#f5f7f6;color:#69736f;font-size:12px;line-height:1.45}
.aae-home .aae-selection-summary{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 12px;border:1px solid rgba(31,90,84,.14);border-radius:13px;background:#f7fbf9;font-size:12px}
.aae-home .aae-selection-summary strong{display:block;color:var(--aae-teal-950);font-size:13px}
.aae-home .aae-selection-summary span{display:block;color:#69736f;margin-top:3px}
.aae-home .aae-selection-check{display:grid!important;place-items:center;width:26px;height:26px;border-radius:50%;background:var(--aae-lime);color:#15302c!important;font-weight:900}
.aae-home .aae-interview-page{background:radial-gradient(circle at 82% 9%,rgba(255,255,255,.12),transparent 25%),linear-gradient(126deg,#217967 0%,#258d79 46%,#2a9a85 100%);color:#fff;min-height:100vh}
.aae-home .aae-interview-nav{background:rgba(18,59,53,.16);border-bottom:1px solid rgba(255,255,255,.16)}
.aae-home .aae-interview-nav .aae-nav{width:min(1440px,calc(100% - 72px));padding-top:18px}
.aae-home .aae-interview-nav .aae-nav-actions a{color:#fff}
.aae-home .aae-interview-nav .aae-nav-actions a:hover{color:#123b35}
.aae-home .aae-interview-main{width:min(1440px,calc(100% - 72px));margin:0 auto;padding:34px 0 100px}
.aae-home .aae-interview-crumb{display:flex;align-items:center;gap:9px;color:rgba(255,255,255,.68);font-size:12px;font-weight:800;margin-bottom:40px}
.aae-home .aae-interview-crumb a,.aae-home .aae-interview-crumb strong{color:#fff}
.aae-home .aae-interview-intro{display:grid;grid-template-columns:minmax(0,1fr) 460px;gap:80px;align-items:end}
.aae-home .aae-interview-eyebrow{display:inline-flex;align-items:center;gap:7px;color:#d8ff91;font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:1.1px}
.aae-home .aae-interview-eyebrow i{width:8px;height:8px;border-radius:50%;background:#f7b940;box-shadow:0 0 0 5px rgba(247,185,64,.16)}
.aae-home .aae-interview-page .aae-interview-intro h1{max-width:850px;margin:16px 0 12px;font-size:clamp(40px,5vw,70px);line-height:.98;letter-spacing:-3px;color:#fff!important}
.aae-home .aae-interview-page .aae-interview-intro p{max-width:760px;color:rgba(255,255,255,.82)!important;font-size:17px;line-height:1.65;margin:0}
.aae-home .aae-interview-meta{display:flex;flex-wrap:wrap;gap:9px;margin-top:26px}
.aae-home .aae-interview-meta span{display:inline-flex;align-items:center;gap:7px;border:1px solid rgba(255,255,255,.28);border-radius:999px;padding:9px 12px;background:rgba(255,255,255,.10);color:#fff;font-size:12px;font-weight:800}
.aae-home .aae-interview-meta svg{width:15px;height:15px;color:var(--aae-lime)}
.aae-home .aae-interview-focus{padding:26px;border:1px solid rgba(255,255,255,.24);border-radius:22px;background:rgba(18,59,53,.30);backdrop-filter:blur(8px)}
.aae-home .aae-interview-focus-label{display:block;color:rgba(255,255,255,.74);font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:1px}
.aae-home .aae-interview-page .aae-interview-focus h3{margin:8px 0 12px;font-size:20px;color:#fff!important}
.aae-home .aae-skill-list{display:flex;flex-wrap:wrap;gap:7px}
.aae-home .aae-skill-list span{padding:7px 10px;border-radius:9px;background:#fff;color:#236250;font-size:12px;font-weight:800}
.aae-home .aae-interview-workspace{display:grid;grid-template-columns:minmax(0,1fr) 520px;gap:32px;margin-top:64px;align-items:start}
.aae-home .aae-interview-booking{position:sticky;top:24px;padding:25px;border:1px solid rgba(31,90,84,.13);border-radius:24px;background:#fff;box-shadow:0 20px 55px rgba(34,67,58,.10)}
.aae-home .aae-interview-booking-head{display:flex;justify-content:space-between;gap:12px;align-items:start;margin-bottom:20px}
.aae-home .aae-interview-booking-head h2{margin:0;font-size:23px;letter-spacing:-.8px;color:#123b35}
.aae-home .aae-interview-price{font-size:22px;font-weight:900;color:#123b35;white-space:nowrap}
.aae-home .aae-interview-price small{display:block;text-align:right;color:#77847f;font-size:11px;font-weight:700;margin-top:3px}
.aae-home .aae-interview-progress{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:20px}
.aae-home .aae-interview-progress span{height:4px;border-radius:99px;background:#dce8e1}
.aae-home .aae-interview-progress span.is-active{background:#21836d}
.aae-home .aae-interview-booking .aae-interview-picker{margin-top:0}
.aae-home .aae-interview-booking .aae-enroll{margin-top:16px;border-radius:13px;padding:14px}
.aae-home .aae-interview-booking .aae-preview-list{display:none}
.aae-home .aae-interview-benefits{display:grid;gap:14px;padding:28px;border-radius:24px;background:#123b35;color:#fff}
.aae-home .aae-interview-page .aae-interview-benefits h2{margin:0 0 5px;font-size:25px;letter-spacing:-.8px;color:#fff!important}
.aae-home .aae-interview-page .aae-interview-benefits p{margin:0 0 10px;color:rgba(255,255,255,.78)!important;font-size:14px;line-height:1.6}
.aae-home .aae-interview-benefit{display:flex;gap:10px;align-items:start;color:rgba(255,255,255,.86);font-size:13px;line-height:1.45}
.aae-home .aae-interview-benefit svg{flex:0 0 auto;margin-top:2px;color:#c7f36d}
.aae-home .aae-interview-info{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:24px}
.aae-home .aae-interview-info article{padding:18px;border:1px solid rgba(31,90,84,.11);border-radius:18px;background:#fff}
.aae-home .aae-interview-info svg{width:19px;height:19px;color:#16745f}
.aae-home .aae-interview-info strong{display:block;margin-top:10px;font-size:14px;color:#123b35}
.aae-home .aae-interview-info span{display:block;margin-top:5px;color:#73817c;font-size:12px;line-height:1.45}
.aae-home .aae-enroll{display:block;text-align:center;border-radius:999px;padding:16px;font-weight:900;background:var(--aae-orange);color:#151515}
.aae-home .aae-preview-list{display:grid;gap:10px;margin-top:18px;font-size:13px;color:#5e6864;font-weight:700}
.aae-home button.aae-enroll{width:100%;border:0;cursor:pointer;font:inherit;font-weight:900;font-size:15px}
.aae-home button.aae-enroll[disabled]{opacity:.6;cursor:not-allowed}
.aae-home .aae-schedule-field{margin-bottom:14px}
.aae-home .aae-schedule-options{display:grid;gap:10px;margin-top:9px}
.aae-home .aae-schedule-option{display:grid;grid-template-columns:20px 1fr;gap:10px;padding:13px;border:1px solid rgba(31,90,84,.18);border-radius:14px;background:#fff;color:#26322f;cursor:pointer}
.aae-home .aae-schedule-option:has(input:checked){border-color:var(--aae-teal-700);box-shadow:inset 0 0 0 1px var(--aae-teal-700);background:#f1faf6}
.aae-home .aae-schedule-option input{margin-top:3px;accent-color:var(--aae-teal-700)}
.aae-home .aae-schedule-option strong,.aae-home .aae-schedule-option span{display:block}
.aae-home .aae-schedule-option span{margin-top:3px;color:#586660;font-size:12px;line-height:1.4}
.aae-home .aae-alert{margin-top:14px;border-radius:16px;padding:12px 14px;font-size:13px;font-weight:700;line-height:1.5}
.aae-home .aae-alert.is-warning{background:#fff7e6;color:#7a4b00;border:1px solid #ffd591}
.aae-home .aae-alert.is-error{background:#fff1f0;color:#8c1d18;border:1px solid #ffccc7}
.aae-home .aae-alert.is-info{background:#eef7f5;color:#1f5a54;border:1px solid #cfe6e1}
.aae-home .aae-alert.is-success{background:#f0f9f1;color:#256029;border:1px solid #b7dfba}

.aae-home .aae-panel{background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:30px;padding:34px;box-shadow:0 18px 45px rgba(45,56,52,.07);margin-bottom:20px}
.aae-home .aae-panel h2{font-size:28px;letter-spacing:-1.1px;margin-bottom:14px}
.aae-home .aae-panel p{color:#68706d;line-height:1.7}
.aae-home .aae-learn-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:20px}
.aae-home .aae-learn-item{display:grid;grid-template-columns:26px 1fr;gap:12px;align-items:start;font-size:14px;color:#454f4c;line-height:1.55}
.aae-home .aae-learn-item i{width:24px;height:24px;display:grid;place-items:center;border-radius:50%;background:var(--aae-lime);color:#15302c;font-style:normal;font-weight:900;font-size:12px}
.aae-home .aae-curriculum-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin:22px 0}
.aae-home .aae-curriculum-summary>div{background:var(--aae-paper);border-radius:20px;padding:18px;text-align:center}
.aae-home .aae-curriculum-summary strong{display:block;font-size:24px;letter-spacing:-1px}
.aae-home .aae-curriculum-summary span{color:#68706d;font-size:12px}
.aae-home .aae-module{border:1px solid rgba(29,61,55,.10);border-radius:22px;margin-bottom:12px;overflow:hidden;background:var(--aae-paper)}
.aae-home .aae-module>summary{display:grid;grid-template-columns:52px 1fr auto;gap:14px;align-items:center;padding:18px 20px;cursor:pointer;list-style:none}
.aae-home .aae-module>summary::-webkit-details-marker{display:none}
.aae-home .aae-module-number{width:44px;height:44px;display:grid;place-items:center;border-radius:14px;background:var(--aae-teal-950);color:var(--aae-lime);font-weight:900;font-size:14px}
.aae-home .aae-module-title strong{display:block;font-size:17px}
.aae-home .aae-module-title small{color:#68706d;font-size:13px}
.aae-home .aae-module-meta{color:#5e6864;font-size:12px;font-weight:800;white-space:nowrap}
.aae-home .aae-lessons{padding:0 20px 14px;background:#fff}
.aae-home .aae-lesson-row{display:grid;grid-template-columns:30px 1fr auto;gap:12px;align-items:center;padding:13px 0;border-top:1px solid rgba(29,61,55,.08)}
.aae-home .aae-lesson-row i{width:28px;height:28px;display:grid;place-items:center;border-radius:50%;background:var(--aae-lime-soft);color:#22403a;font-style:normal;font-weight:900;font-size:11px}
.aae-home .aae-lesson-row strong{display:block;font-size:14px}
.aae-home .aae-lesson-row small{color:#727a77;font-size:12px}
.aae-home .aae-lesson-row>span{color:#5e6864;font-size:12px;font-weight:800;white-space:nowrap}
.aae-home .aae-lesson-row.is-project i{background:var(--aae-orange)}
.aae-home .aae-side-card{background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:28px;padding:26px;box-shadow:0 18px 45px rgba(45,56,52,.07);margin-bottom:18px}
.aae-home .aae-side-card h3{font-size:19px;letter-spacing:-.5px;margin-bottom:16px}
.aae-home .aae-side-list{display:grid;gap:12px;font-size:14px}
.aae-home .aae-side-list>div{display:flex;align-items:center;justify-content:space-between;gap:12px;padding-bottom:11px;border-bottom:1px solid rgba(29,61,55,.08)}
.aae-home .aae-side-list span{color:#68706d}
.aae-home .aae-side-card.is-certificate{background:var(--aae-teal-950);color:#fff;border-color:transparent}
.aae-home .aae-side-card.is-certificate h3{color:#fff}
.aae-home .aae-side-card.is-certificate p{color:#f1f7f5;line-height:1.65;font-size:14px}
.aae-home .aae-certificate-badge{width:48px;height:48px;display:grid;place-items:center;border-radius:16px;background:var(--aae-lime);color:#15302c;font-size:22px;font-weight:900;margin-bottom:16px}

.aae-home .aae-about-hero-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:52px;align-items:center;margin-top:26px}
.aae-home .aae-about-actions{display:flex;gap:14px;flex-wrap:wrap;margin-top:28px}
.aae-home .aae-about-visual{min-height:420px}
.aae-home .aae-about-visual .aae-orbit{top:6px;right:10px}
.aae-home .aae-about-visual .aae-student-photo{top:34px;right:42px}
.aae-home .aae-about-visual .aae-about-float.two{top:auto;bottom:-4px;right:-6px}
.aae-home .aae-about-float{position:absolute;background:#fff;color:var(--aae-ink);border-radius:22px;padding:16px 18px;box-shadow:0 22px 48px rgba(13,59,54,.24)}
.aae-home .aae-about-float strong{display:block;font-size:20px;letter-spacing:-.8px}
.aae-home .aae-about-float span{color:#68706d;font-size:12px}
.aae-home .aae-about-float.one{left:-14px;bottom:44px}
.aae-home .aae-about-float.two{right:-8px;top:18px;max-width:210px}
.aae-home .aae-story-split{display:grid;grid-template-columns:.9fr 1.1fr;gap:44px;margin-top:56px;align-items:start}
.aae-home .aae-story-copy p{color:#68706d;line-height:1.75;margin-bottom:16px}
.aae-home .aae-purpose-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.aae-home .aae-purpose-card{padding:38px;border-radius:32px;position:relative;overflow:hidden;min-height:280px}
.aae-home .aae-purpose-card.is-dark{background:var(--aae-teal-950);color:#fff}
.aae-home .aae-purpose-card.is-lime{background:var(--aae-lime);color:var(--aae-ink)}
.aae-home .aae-purpose-card.is-dark>span,.aae-home .aae-purpose-card.is-dark h2{color:#fff!important}
.aae-home .aae-purpose-icon{font-size:13px;font-weight:900;letter-spacing:1.5px;opacity:.6}
.aae-home .aae-purpose-card>span{display:block;margin:16px 0 10px;font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:1.4px}
.aae-home .aae-purpose-card h2{font-size:27px;line-height:1.2;letter-spacing:-1.1px;margin-bottom:12px}
.aae-home .aae-purpose-card.is-dark p{color:rgba(255,255,255,.68);line-height:1.7}
.aae-home .aae-purpose-card.is-lime p{color:#2f3a37;line-height:1.7}
.aae-home .aae-approach-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:18px;margin-top:36px}
.aae-home .aae-approach-card{background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:26px;padding:26px;box-shadow:0 16px 38px rgba(45,56,52,.06)}
.aae-home .aae-approach-card>span{font-size:12px;font-weight:900;color:var(--aae-teal-700);letter-spacing:1.4px}
.aae-home .aae-approach-card i{display:grid;place-items:center;width:44px;height:44px;border-radius:14px;background:var(--aae-lime-soft);color:#22403a;font-style:normal;font-weight:900;margin:14px 0 16px}
.aae-home .aae-approach-card h3{font-size:18px;letter-spacing:-.6px;margin-bottom:8px}
.aae-home .aae-approach-card p{color:#68706d;font-size:14px;line-height:1.6}
.aae-home .aae-values-layout{display:grid;grid-template-columns:.85fr 1.15fr;gap:48px;align-items:start}
.aae-home .aae-values-list{display:grid;gap:14px}
.aae-home .aae-values-list article{display:grid;grid-template-columns:52px 1fr;gap:16px;background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:24px;padding:22px}
.aae-home .aae-values-list article>span{width:44px;height:44px;display:grid;place-items:center;border-radius:14px;background:var(--aae-teal-950);color:var(--aae-lime);font-weight:900;font-size:14px}
.aae-home .aae-values-list h3{font-size:17px;letter-spacing:-.5px;margin-bottom:6px}
.aae-home .aae-values-list p{color:#68706d;font-size:14px;line-height:1.6}
.aae-home .aae-experience-panel{background:var(--aae-teal-950);color:#fff;border-radius:36px;padding:48px;display:grid;grid-template-columns:96px 1fr;gap:28px;align-items:center;position:relative;overflow:hidden}
.aae-home .aae-experience-mark{width:96px;height:96px;display:grid;place-items:center;border-radius:28px;background:var(--aae-lime);color:#15302c;font-size:40px;font-weight:900}
.aae-home .aae-experience-panel .aae-kicker{color:#d8ff91!important}
.aae-home .aae-experience-panel h2{font-size:30px;letter-spacing:-1.2px;margin-top:8px;color:#fff!important}
.aae-home .aae-experience-points{grid-column:1/-1;display:flex;flex-wrap:wrap;gap:10px;margin-top:8px}
.aae-home .aae-experience-points span{border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.06);border-radius:999px;padding:10px 16px;font-size:13px;font-weight:800}

.aae-home .aae-contact-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:24px;margin-top:36px;align-items:start}
.aae-home .aae-form-card{background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:30px;padding:34px;box-shadow:0 18px 45px rgba(45,56,52,.07)}
.aae-home .aae-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:22px}
.aae-home .aae-field{display:grid;gap:8px}
.aae-home .aae-field.is-wide{grid-column:1/-1}
.aae-home .aae-field label{font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:1.1px;color:#5e6864}
.aae-home .aae-field input,.aae-home .aae-field textarea,.aae-home .aae-field select{border:1px solid rgba(31,90,84,.18);border-radius:16px;padding:14px 16px;font:inherit;font-size:14px;color:var(--aae-ink);background:#fff;outline:0}
.aae-home .aae-field textarea{min-height:120px;resize:vertical}
.aae-home .aae-field input:focus,.aae-home .aae-field textarea:focus,.aae-home .aae-field select:focus{border-color:var(--aae-teal-700)}
/* Field-level validation error. Palette is taken from .aae-alert.is-error so inline field
   errors and block alerts read as the same error state. */
.aae-home .aae-field input.is-invalid,.aae-home .aae-field textarea.is-invalid{border-color:#8c1d18}
.aae-home .aae-field-error{color:#8c1d18;font-size:12px;font-weight:700;line-height:1.5}
.aae-home .aae-form-actions .aae-btn[disabled]{opacity:.65;cursor:not-allowed}
.aae-home .aae-form-actions{margin-top:22px}
.aae-home .aae-contact-side{display:grid;gap:16px}
.aae-home .aae-contact-card{background:var(--aae-teal-950);color:#fff;border-radius:30px;padding:32px;position:relative;overflow:hidden}
.aae-home .aae-contact-card::after{content:"";position:absolute;width:200px;height:200px;border:42px solid rgba(216,255,145,.12);border-radius:50%;right:-70px;bottom:-80px}
.aae-home .aae-contact-card h3{font-size:22px;letter-spacing:-.8px;margin-bottom:16px}
.aae-home .aae-contact-row{display:grid;grid-template-columns:44px 1fr;gap:14px;align-items:start;padding:14px 0;border-top:1px solid rgba(255,255,255,.14)}
.aae-home .aae-contact-row i{width:40px;height:40px;display:grid;place-items:center;border-radius:14px;background:var(--aae-lime);color:#15302c;font-style:normal;font-weight:900;font-size:14px}
.aae-home .aae-contact-row strong{display:block;font-size:15px}
.aae-home .aae-contact-row span{color:rgba(255,255,255,.66);font-size:13px}
.aae-home .aae-hours-card{background:var(--aae-lime);border-radius:30px;padding:30px}
.aae-home .aae-hours-card h3{font-size:20px;letter-spacing:-.7px;margin-bottom:14px}
.aae-home .aae-hours-row{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:11px 0;border-top:1px solid rgba(31,90,84,.16);font-size:14px;font-weight:700;color:#2f3a37}

.aae-home .aae-info-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:44px;align-items:center;margin-top:8px}
.aae-home .aae-info-photo{border-radius:32px;overflow:hidden;box-shadow:0 26px 56px rgba(20,63,57,.18);min-height:320px}
.aae-home .aae-info-photo img{width:100%;height:100%;object-fit:cover;display:block}
.aae-home .aae-info-copy p{color:#68706d;line-height:1.75;margin-bottom:16px}
.aae-home .aae-info-points{display:grid;gap:12px;margin-top:26px}
.aae-home .aae-info-point{display:grid;grid-template-columns:30px 1fr;gap:14px;align-items:start;background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:20px;padding:18px 20px}
.aae-home .aae-info-point i{width:28px;height:28px;display:grid;place-items:center;border-radius:50%;background:var(--aae-lime);color:#15302c;font-style:normal;font-weight:900;font-size:12px}
.aae-home .aae-info-point strong{display:block;font-size:16px;margin-bottom:4px}
.aae-home .aae-info-point span{color:#68706d;font-size:14px;line-height:1.6}

.aae-home .aae-shell-body{padding:44px 0 88px;background:var(--aae-paper)}
.aae-home .aae-shell-panel{width:min(1240px,calc(100% - 80px));margin:0 auto;background:#fff;border:1px solid rgba(31,90,84,.10);border-radius:32px;padding:12px;box-shadow:0 20px 50px rgba(45,56,52,.08);overflow:hidden}
.aae-home .aae-auth-shell-panel{padding:0;border:0;border-radius:34px;background:transparent;box-shadow:none;overflow:visible}
.aae-home .aae-auth-shell-sign-in{width:min(620px,calc(100% - 80px))}
.aae-home .aae-auth-shell-sign-up{width:min(980px,calc(100% - 80px))}
.aae-home .aae-exam-auth{width:100%;background:transparent;border-radius:34px;overflow:visible}
.aae-home .aae-exam-auth>div[class*="max-w-md"]{max-width:520px!important;margin:0 auto!important;margin-top:0!important;margin-bottom:0!important;background:#fff!important;border-radius:30px!important;box-shadow:none!important}
.aae-home .aae-exam-auth>div[class*="max-w-md"]>div:first-child{margin-top:0!important}
.aae-home .aae-exam-auth .academy-signup-page{min-height:auto!important;margin:0!important;padding:0!important;background:transparent!important}
.aae-home .aae-exam-auth .academy-signup-card{display:grid!important;grid-template-columns:minmax(300px,.95fr) minmax(420px,1.05fr)!important;width:100%!important;max-width:100%!important;margin:0!important;border-radius:30px!important;box-shadow:0 20px 50px rgba(45,56,52,.08)!important}
.aae-home .aae-exam-auth .academy-signup-info{background:var(--aae-teal-950)!important}
.aae-home .aae-exam-auth .academy-signup-info h4{color:#fff!important}
.aae-home .aae-exam-auth .academy-signup-info p{color:rgba(255,255,255,.82)!important}
.aae-home .aae-exam-auth .academy-signup-form{border-radius:0 30px 30px 0!important}
.aae-home .aae-exam-auth .academy-signup-page>div[class*="mt-6"][class*="text-center"]{margin:0!important;padding:22px 30px 34px!important;border-top:1px solid rgba(31,90,84,.10)!important;background:#f7fbf3!important;border-radius:0 0 30px 30px}
.aae-home .aae-exam-auth .academy-signup-page>div[class*="mt-6"][class*="text-center"] p{color:#50625e!important}
.aae-home .aae-exam-auth .academy-signup-page>div[class*="mt-6"][class*="text-center"] a{color:var(--aae-teal-700)!important;font-weight:900!important}
.aae-home .aae-exam-auth button[id="sign-in-button"],.aae-home .aae-exam-auth #Register-signup-button{background:var(--aae-orange)!important;color:#151515!important;box-shadow:0 16px 34px rgba(255,176,48,.22)!important}
.aae-home .aae-exam-auth button[class*="border-blue-500"]{border-color:var(--aae-teal-700)!important;color:var(--aae-teal-700)!important}
.aae-home .aae-exam-auth a,.aae-home .aae-exam-auth button[type="button"]{color:var(--aae-teal-700)}

@media(max-width:1080px){
.aae-home .aae-page-head{width:min(100% - 40px,930px)}
.aae-home .aae-course-detail-skeleton-nav,.aae-home .aae-course-detail-skeleton-content{width:min(100% - 40px,930px)}
.aae-home .aae-course-detail-skeleton-grid{grid-template-columns:1fr}
.aae-home .aae-contact-grid,.aae-home .aae-info-grid{grid-template-columns:1fr;gap:28px}
.aae-home .aae-shell-panel,.aae-home .aae-auth-shell-panel{width:min(100% - 40px,930px)}
.aae-home .aae-catalog{grid-template-columns:1fr}
.aae-home .aae-filters{position:static}
.aae-home .aae-category-grid{grid-template-columns:repeat(2,1fr)}
.aae-home .aae-detail-grid,.aae-home .aae-about-hero-grid,.aae-home .aae-story-split,.aae-home .aae-values-layout{grid-template-columns:1fr;gap:32px}
.aae-home .aae-interview-intro,.aae-home .aae-interview-workspace{grid-template-columns:1fr;gap:28px}
.aae-home .aae-interview-booking{position:static}
.aae-home .aae-curriculum-summary,.aae-home .aae-approach-grid{grid-template-columns:1fr 1fr}
.aae-home .aae-purpose-grid{grid-template-columns:1fr}
.aae-home .aae-about-float.one,.aae-home .aae-about-float.two{position:static;margin-top:14px;max-width:none}
}
@media(max-width:640px){
.aae-home .aae-course-detail-loader{padding-top:12px}
.aae-home .aae-course-detail-skeleton-nav{height:58px;grid-template-columns:1fr 1fr;gap:12px}
.aae-home .aae-course-detail-skeleton-nav>*:last-child{grid-column:2}
.aae-home .aae-course-detail-skeleton-content{padding-top:36px}
.aae-home .aae-course-detail-skeleton-title{width:100%;height:42px}
.aae-home .aae-course-detail-skeleton-subtitle,.aae-home .aae-course-detail-skeleton-subtitle.short{width:100%}
.aae-home .aae-course-detail-skeleton-panel{padding:24px}
.aae-home .aae-section{padding:64px 0}
.aae-home .aae-catalog .aae-course-grid{grid-template-columns:1fr}
.aae-home .aae-panel,.aae-home .aae-purpose-card{padding:24px}
.aae-home .aae-learn-grid,.aae-home .aae-curriculum-summary,.aae-home .aae-approach-grid,.aae-home .aae-category-grid,.aae-home .aae-form-grid{grid-template-columns:1fr}
.aae-home .aae-form-card,.aae-home .aae-contact-card,.aae-home .aae-hours-card{padding:24px}
.aae-home .aae-module>summary{grid-template-columns:44px 1fr;row-gap:8px}
.aae-home .aae-module-meta{grid-column:1/-1}
.aae-home .aae-experience-panel{grid-template-columns:1fr;padding:30px}
.aae-home .aae-page-head h1{font-size:36px;letter-spacing:-1.6px}
.aae-home .aae-interview-main{width:min(100% - 28px,560px);padding-top:24px}
.aae-home .aae-interview-intro h1{font-size:42px;letter-spacing:-2px}
.aae-home .aae-interview-info{grid-template-columns:1fr}
.aae-home .aae-interview-booking{padding:20px}
.aae-home .aae-interviewer-list{grid-template-columns:1fr}
.aae-home .aae-exam-auth .academy-signup-card{grid-template-columns:minmax(0,1fr)!important}
.aae-home .aae-exam-auth .academy-signup-info{display:none!important}
.aae-home .aae-exam-auth .academy-signup-form{border-radius:30px 30px 0 0!important}
}
`;

/** Content defaults per page, matching the supplied HTML, re-cast as exam material. */
const PAGE_DEFAULTS = {
  [EXAM_PAGES_TYPES.COURSES]: {
    crumb: 'Exams',
    eyebrow: 'Explore AVPS certification exams',
    title: 'Find the right exam for your next certification.',
    subtitle: 'Browse proctored assessments for beginners, working professionals and candidates preparing for recognised technology roles.',
    searchPlaceholder: 'Search web development, cloud, Java, testing...',
    searchButtonLabel: 'Search exams',
    countValue: '120+',
    countLabel: 'certification exams',
    kicker: 'Exam catalog',
    heading: 'Pick an assessment that matches your goal.',
    lead: 'Filter by category, level and delivery mode. Every exam includes a question bank, timed sections and automatic scoring.',
    filters: [
      { heading: 'Category', options: ['Web Development', 'Cloud & DevOps', 'Data & AI', 'UI/UX Design', 'Software Testing'] },
      { heading: 'Level', options: ['Beginner', 'Intermediate', 'Advanced'] },
      { heading: 'Delivery', options: ['Proctored online', 'Scheduled window', 'Instant attempt'] }
    ],
    resultsLabel: 'Showing 6 popular exams',
    sortOptions: ['Most popular', 'Newest', 'Beginner first', 'Shortest duration'],
    categoriesKicker: 'Browse by category',
    categoriesTitle: 'Choose the skill area you want to certify.',
    categories: [
      { icon: '</>', title: 'Development', meta: '12 exams' },
      { icon: '☁', title: 'Cloud & DevOps', meta: '8 exams' },
      { icon: 'AI', title: 'Data & AI', meta: '7 exams' },
      { icon: 'UI', title: 'Design', meta: '6 exams' },
      { icon: '✓', title: 'Testing', meta: '7 exams' }
    ]
  },

  [EXAM_PAGES_TYPES.COURSE_DETAIL]: {
    crumb: 'Full-Stack Developer Certification',
    eyebrow: 'Most popular certification',
    title: 'Full-Stack Developer Certification',
    subtitle: 'A proctored assessment covering modern front-end, Java and Spring Boot APIs, relational data modelling and deployment readiness.',
    pills: ['Proctored', '90 minutes', '60 questions', 'Certificate included'],
    instructorInitials: 'AK',
    instructorName: 'Arun Kumar',
    instructorRole: 'Chief Examiner, Engineering · 9+ years experience',
    priceLabel: '₹12,999',
    previewTitle: 'Book your proctored attempt',
    enrollLabel: 'Register for this exam',
    previewList: ['Scheduled attempt window', 'Live proctoring', 'Section-wise analytics', 'Verified certificate'],
    overviewTitle: 'Exam overview',
    overview: 'This assessment validates whether you can build and reason about a production-style web application. Questions are drawn from a reviewed bank spanning interface work, API design, data modelling and release practices, and are scored automatically the moment the attempt closes.',
    learnTitle: 'What this exam assesses',
    learnItems: [
      'Responsive interface work with HTML, CSS and JavaScript.',
      'Java fundamentals, object-oriented design and clean code.',
      'REST API design with Spring Boot and layered architecture.',
      'Relational modelling, querying and data integrity.',
      'Version control, testing and debugging judgement.',
      'Deployment readiness and production configuration.'
    ],
    curriculumKicker: 'Full blueprint',
    curriculumTitle: 'Exam blueprint',
    curriculumMeta: '6 sections · 60 questions · 90 minutes',
    summary: [
      { value: '60', label: 'Questions' },
      { value: '90m', label: 'Duration' },
      { value: '6', label: 'Sections' },
      { value: '70%', label: 'Pass mark' }
    ],
    modules: [
      {
        number: '01',
        title: 'Web foundations',
        summary: 'Semantic markup, layout and responsive behaviour.',
        meta: '10 questions · 14m',
        lessons: [
          { title: 'Request and response fundamentals', detail: 'Browsers, servers and the role of HTTP.', meta: '4 questions' },
          { title: 'Semantic and accessible markup', detail: 'Structure pages with meaningful elements.', meta: '3 questions' },
          { title: 'Flexbox, Grid and responsive layout', detail: 'Layouts that adapt across breakpoints.', meta: '3 questions' }
        ]
      },
      {
        number: '02',
        title: 'JavaScript essentials',
        summary: 'Language behaviour, the DOM and async work.',
        meta: '12 questions · 18m',
        lessons: [
          { title: 'Types, scope and coercion', detail: 'Reason about values and expressions.', meta: '5 questions' },
          { title: 'DOM interaction and events', detail: 'Read and update the interface safely.', meta: '4 questions' },
          { title: 'Promises and async/await', detail: 'Consume remote APIs correctly.', meta: '3 questions' }
        ]
      },
      {
        number: '03',
        title: 'Java and object-oriented design',
        summary: 'Core language and modelling judgement.',
        meta: '12 questions · 18m',
        lessons: [
          { title: 'Classes, objects and encapsulation', detail: 'Model behaviour with reusable types.', meta: '5 questions' },
          { title: 'Inheritance, interfaces, polymorphism', detail: 'Design flexible object graphs.', meta: '4 questions' },
          { title: 'Collections and exception handling', detail: 'Manage data and failure paths.', meta: '3 questions' }
        ]
      },
      {
        number: '04',
        title: 'Spring Boot REST APIs',
        summary: 'Endpoint design, layering and validation.',
        meta: '12 questions · 18m',
        lessons: [
          { title: 'Resource-based endpoint design', detail: 'Correct HTTP semantics and status codes.', meta: '5 questions' },
          { title: 'Services, repositories and layering', detail: 'Separate business logic from data access.', meta: '4 questions' },
          { title: 'DTOs, validation and error handling', detail: 'Protect API boundaries.', meta: '3 questions', isProject: true }
        ]
      },
      {
        number: '05',
        title: 'Database integration',
        summary: 'Relational design, SQL and JPA mapping.',
        meta: '8 questions · 12m',
        lessons: [
          { title: 'Schema design, keys and relationships', detail: 'Turn requirements into tables.', meta: '4 questions' },
          { title: 'Queries, pagination and aggregation', detail: 'Efficient, correct result sets.', meta: '4 questions' }
        ]
      },
      {
        number: '06',
        title: 'Delivery and deployment',
        summary: 'Testing, configuration and release judgement.',
        meta: '6 questions · 10m',
        lessons: [
          { title: 'Testing and debugging workflow', detail: 'Validate before release.', meta: '3 questions' },
          { title: 'Production configuration', detail: 'Environment settings and rollout.', meta: '3 questions', isProject: true }
        ]
      }
    ],
    examinerTitle: 'Meet your examiner',
    examinerBio: 'Arun has more than nine years building enterprise applications and authoring assessments. He reviews every question in this bank for clarity, fairness and real-world relevance.',
    sideTitle: 'This exam includes',
    sideList: [
      { label: 'Duration', value: '90 minutes' },
      { label: 'Questions', value: '60' },
      { label: 'Sections', value: '6' },
      { label: 'Proctoring', value: 'Live' },
      { label: 'Result', value: 'Immediate' },
      { label: 'Language', value: 'English' }
    ],
    certificateTitle: 'Earn a verified certificate',
    certificateBody: 'Clear the pass mark and receive a shareable, verifiable AVPS certificate with your section-wise breakdown.',
    helpTitle: 'Need exam guidance?',
    helpBody: 'Talk to a certification advisor before registering and confirm this assessment matches your current level.',
    helpLabel: 'Talk to an advisor',
    helpUrl: '/contact'
  },

  [EXAM_PAGES_TYPES.ABOUT]: {
    crumb: 'About',
    eyebrow: 'Built for credible assessment',
    title: 'Assessment that turns preparation into proof.',
    subtitle: '{companyName} runs secure, proctored certification exams with reviewed question banks, automatic scoring and verifiable results that stand up to scrutiny.',
    primaryLabel: 'Browse exams',
    primaryUrl: '/course',
    secondaryLabel: 'How assessment works',
    secondaryUrl: '#approach',
    floatOneValue: '—',
    floatOneLabel: 'published exams',
    floatTwoValue: 'Integrity first',
    floatTwoLabel: 'Proctored attempts and audited results',
    stats: [],
    storyKicker: 'Who we are',
    storyTitle: 'An assessment platform built around fairness, not friction.',
    storyParagraphs: [
      'We believe a certification is only worth what its assessment can defend. Every exam is assembled from a reviewed question bank, mapped to a published blueprint, and scored the same way for every candidate.',
      'From programming fundamentals to cloud, testing and domain certification, each exam states its duration, section weighting and pass mark up front, so candidates always know exactly what is being measured.'
    ],
    purpose: [
      { icon: '01', label: 'Our mission', title: 'Make credible certification accessible.', body: 'Run assessments that candidates can sit from anywhere while keeping results defensible and consistently scored.' },
      { icon: '02', label: 'Our vision', title: 'Make every result independently verifiable.', body: 'Publish certificates that any employer can verify, backed by transparent blueprints and section-wise evidence.' }
    ],
    approachKicker: 'How assessment works',
    approachTitle: 'A clear path from registration to verified result.',
    approachLead: 'Each exam combines a published blueprint, a scheduled proctored attempt, automatic scoring and a verifiable certificate.',
    approach: [
      { number: '01', icon: '◎', title: 'Choose your exam', description: 'Pick a certification by domain, level and duration, and review its published blueprint.' },
      { number: '02', icon: '▶', title: 'Book an attempt', description: 'Reserve a proctored window and complete the system check before you begin.' },
      { number: '03', icon: '⌘', title: 'Attempt securely', description: 'Sit timed sections with monitoring and auto-submit safeguards in place.' },
      { number: '04', icon: '✓', title: 'Get verified results', description: 'Receive automatic scoring, section analytics and a shareable certificate.' }
    ],
    valuesKicker: 'What matters to us',
    valuesTitle: 'Built around the needs of serious candidates.',
    valuesLead: 'Four principles keep our assessments fair, transparent and worth putting on a CV.',
    valuesCtaLabel: 'View all exams',
    valuesCtaUrl: '/course',
    values: [
      { number: '01', title: 'Transparency over guesswork', description: 'Published blueprints, section weights and pass marks before you register.' },
      { number: '02', title: 'Integrity over convenience', description: 'Proctored attempts with auditable safeguards so every result means the same thing.' },
      { number: '03', title: 'Evidence over scores', description: 'Section-wise analytics show exactly where you were strong and where you were not.' },
      { number: '04', title: 'Verifiable outcomes', description: 'Certificates any employer can independently check.' }
    ],
    experienceKicker: 'The {companyName} difference',
    experienceTitle: 'More than a score. Evidence you can share.',
    experiencePoints: ['Published blueprint', 'Reviewed question banks', 'Live proctoring', 'Automatic scoring', 'Section analytics', 'Verifiable certificate']
  }
};

PAGE_DEFAULTS[EXAM_PAGES_TYPES.CONTACT] = {
  crumb: 'Contact',
  eyebrow: 'Candidate support',
  title: 'Talk to us before or after your attempt.',
  subtitle: 'Questions about scheduling, proctoring requirements, results or certificate verification — the certification desk will get back to you.',
  formTitle: 'Send us a message',
  formLead: 'Share your exam reference if your query is about a specific attempt.',
  submitLabel: 'Send message',
  fields: [
    { label: 'Full name', placeholder: 'Your name', type: 'text' },
    { label: 'Email', placeholder: 'you@example.com', type: 'email' },
    { label: 'Exam reference', placeholder: 'Optional', type: 'text' },
    { label: 'Topic', placeholder: 'Scheduling, results, certificate…', type: 'text' },
    { label: 'Message', placeholder: 'How can we help?', type: 'textarea', wide: true }
  ],
  contactTitle: 'Certification desk',
  contactRows: [
    { icon: '✉', label: 'Email', value: 'Not available' },
    { icon: '☎', label: 'Phone', value: 'Not available' },
    { icon: '⌖', label: 'Address', value: 'Not available' }
  ],
  hoursTitle: 'Support hours',
  hours: [
    { label: 'Monday – Friday', value: '9:00 – 19:00' },
    { label: 'Saturday', value: '10:00 – 15:00' },
    { label: 'Exam-day support', value: '24/7' }
  ]
};

PAGE_DEFAULTS[EXAM_PAGES_TYPES.INFO] = {
  crumb: 'Exam readiness',
  eyebrow: 'Preparing for your attempt',
  title: 'Know exactly what the exam measures.',
  subtitle: 'Every certification publishes its blueprint up front so preparation is targeted rather than guesswork.',
  bodyTitle: 'What to expect',
  paragraphs: [
    'Each exam is assembled from a reviewed question bank mapped to a published blueprint, so the weighting of every section is known before you register.',
    'Attempts are timed per section with auto-submit on expiry, and scoring is automatic — your section-wise breakdown is available the moment the attempt closes.'
  ],
  points: [
    { title: 'Published blueprint', description: 'Section weights, question counts and the pass mark are stated before registration.' },
    { title: 'Timed sections', description: 'Sectional timing with auto-submit keeps every attempt consistent.' },
    { title: 'Reviewed questions', description: 'Domain examiners author and validate each item for clarity and fairness.' },
    { title: 'Immediate evidence', description: 'Automatic scoring with a section-wise breakdown and verifiable certificate.' }
  ]
};

PAGE_DEFAULTS[EXAM_PAGES_TYPES.SHELL] = {
  crumb: 'Exam Portal',
  eyebrow: 'Exam Portal',
  title: 'Your account',
  subtitle: ''
};

const pageDefaultsFor = (type) => PAGE_DEFAULTS[type] || PAGE_DEFAULTS[EXAM_PAGES_TYPES.COURSES];

/** Resolves `key` from authored content, then the page's design default. */
const pageValue = (content, defaults, key) => {
  const value = content?.[key];
  if (value === undefined || value === null || value === '') return defaults[key];
  if (Array.isArray(value) && value.length === 0) return defaults[key];
  return value;
};

const DEFAULT_EXAM_COMPANY_NAME = 'AVPS Academy';

const replaceExamCompanyName = (value, companyName) => (
  typeof value === 'string'
    ? value.replace(/\{companyName\}|\bAVPS Academy\b|\bAVPS\b/g, companyName)
    : value
);

export const resolveExamAboutContent = (content, defaults, data = {}) => {
  const companyName = getExamCompanyName(data.company, DEFAULT_EXAM_COMPANY_NAME);
  const liveProducts = Array.isArray(data.products) ? visibleItems(data.products) : null;
  const examCount = liveProducts
    ? { value: data.loading ? '...' : String(liveProducts.length), label: 'published exams' }
    : null;

  return {
    ...content,
    subtitle: replaceExamCompanyName(pageValue(content, defaults, 'subtitle'), companyName),
    floatOneValue: examCount?.value || replaceExamCompanyName(pageValue(content, defaults, 'floatOneValue'), companyName),
    floatOneLabel: examCount?.label || replaceExamCompanyName(pageValue(content, defaults, 'floatOneLabel'), companyName),
    stats: [],
    experienceKicker: replaceExamCompanyName(pageValue(content, defaults, 'experienceKicker'), companyName)
  };
};

const formatContactPhone = (contactPerson = {}) => {
  const phoneNumber = String(contactPerson.phoneNumber || '').trim();
  if (!phoneNumber) return '';
  if (phoneNumber.startsWith('+')) return phoneNumber;

  const countryCode = String(contactPerson.countryCode || '').trim().replace(/^\+/, '');
  return countryCode ? '+' + countryCode + ' ' + phoneNumber : phoneNumber;
};

const formatCompanyAddress = (address = {}) => [
  address.streetName1,
  address.streetName2,
  address.city,
  address.state,
  address.country,
  address.zipcode
].filter(Boolean).join(', ');

/** Replaces the sample Certification desk values with the active company's live details. */
const companyContactRows = (company, fallbackRows = []) => {
  if (!company) return fallbackRows;

  const companyDetails = company.companyDetails || company;
  const contactPerson = company.contactPerson || companyDetails.contactPerson || {};
  const address = company.address
    || companyDetails.address
    || company.addressList?.[0]
    || companyDetails.addressList?.[0]
    || contactPerson.addressList?.[0]
    || {};
  const fallbackByLabel = new Map(
    visibleItems(fallbackRows).map((row) => [String(row.label || '').toLowerCase(), row])
  );
  const rows = [
    { label: 'Email', value: contactPerson.email || '' },
    { label: 'Phone', value: formatContactPhone(contactPerson) },
    { label: 'Address', value: formatCompanyAddress(address) }
  ];

  return rows.map((row) => ({
    ...(fallbackByLabel.get(row.label.toLowerCase()) || {}),
    ...row,
    value: row.value || 'Not available'
  }));
};

const WORKING_DAY_LABELS = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday'
};

const formatWorkingScheduleTime = (value) => {
  const time = String(value || '').trim();
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return time;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return time;

  return `${hour % 12 || 12}:${match[2]} ${hour >= 12 ? 'PM' : 'AM'}`;
};

/** Builds the hours card from the active company's working schedule. */
export const companyWorkingHoursRows = (company, fallbackRows = []) => {
  if (!company) return fallbackRows;

  const schedule = company.workingSchedule || company.companyDetails?.workingSchedule;
  if (!schedule) return fallbackRows;

  const workingDays = (Array.isArray(schedule.workingDays) ? schedule.workingDays : [])
    .map((day) => {
      const key = String(day || '').trim().toUpperCase();
      return WORKING_DAY_LABELS[key] || key;
    })
    .filter(Boolean);
  const openHours = formatWorkingScheduleTime(schedule.openHours);
  const closeHours = formatWorkingScheduleTime(schedule.closeHours);
  const hours = openHours && closeHours
    ? `${openHours} – ${closeHours}`
    : openHours || closeHours || 'Not available';

  return [{
    label: workingDays.length ? workingDays.join(', ') : 'Working days',
    value: hours
  }];
};

const inquiryFieldRole = (field = {}) => {
  const label = String(field.label || '').toLowerCase();
  const type = String(field.type || '').toLowerCase();
  if (type === 'email' || label.includes('email')) return 'email';
  if (type === 'tel' || label.includes('phone') || label.includes('mobile')) return 'phoneNumber';
  if (type === 'textarea' || label.includes('message') || label.includes('query') || label.includes('comment')) return 'message';
  if (label.includes('reference')) return 'reference';
  if (label.includes('topic') || label.includes('subject')) return 'topic';
  if (label.includes('name')) return 'fullName';
  return 'extra';
};

/** Converts the content-driven exam contact form into the standard create-inquiry payload. */
export const buildExamInquiryPayload = (fields = [], values = {}, company = {}) => {
  const companyDetails = company?.companyDetails || company || {};
  const valueForRole = (role) => {
    const index = fields.findIndex((field) => inquiryFieldRole(field) === role);
    return index < 0 ? '' : String(values[index] || '').trim();
  };
  const fullName = valueForRole('fullName');
  const [firstName = '', ...lastNameParts] = fullName.split(/\s+/).filter(Boolean);
  const operatingLocation = companyDetails?.operatingLocation?.code;
  const countryCode = operatingLocation === 'IND' ? '+91' : operatingLocation === 'US' ? '+1' : '';
  const queryParts = [];
  const topic = valueForRole('topic');
  const reference = valueForRole('reference');
  const message = valueForRole('message');

  if (topic) queryParts.push(`Topic: ${topic}`);
  if (reference) queryParts.push(`Exam reference: ${reference}`);
  fields.forEach((field, index) => {
    if (inquiryFieldRole(field) !== 'extra') return;
    const value = String(values[index] || '').trim();
    if (value) queryParts.push(`${field.label}: ${value}`);
  });
  if (message) queryParts.push(message);

  return {
    user: {
      firstName,
      lastName: lastNameParts.join(' '),
      email: valueForRole('email'),
      phoneNumber: valueForRole('phoneNumber').replace(/\D/g, ''),
      countryCode,
      contactPersonList: []
    },
    query: queryParts.join('\n\n'),
    company: {
      primaryId: companyDetails?.primaryId,
      name: companyDetails?.name,
      url: companyDetails?.url,
      domain: { type: companyDetails?.domain?.type || companyDetails?.domainType || 'EDUCATION' }
    },
    ...buildInquiryNotificationFields(companyDetails)
  };
};

const COURSE_ART = ['a1', 'a2', 'a3'];

const firstImageUrl = (value) => {
  if (!value) return '';
  const rawValue = typeof value === 'string'
    ? value
    : value.displayLink || value.previewUrl || value.url || value.storedLink || '';
  if (!rawValue) return '';
  return /^https?:\/\//i.test(rawValue)
    ? rawValue
    : `https://managemybiz.s3.amazonaws.com/${rawValue}`;
};

/** Resolve the product image across the response shapes used by the API. */
const getProductImageUrl = (product = {}) => {
  const sources = [
    product,
    product.product,
    product.scheduledProduct,
    product.scheduledProduct?.product
  ].filter(Boolean);

  for (const source of sources) {
    const imageFromContents = (Array.isArray(source.imageContents) ? source.imageContents : [])
      .map(firstImageUrl)
      .find(Boolean);
    const imageFromList = (Array.isArray(source.imageUrlsList) ? source.imageUrlsList : [])
      .map(firstImageUrl)
      .find(Boolean);
    const imageUrl = imageFromContents
      || imageFromList
      || firstImageUrl(source.imageURL)
      || firstImageUrl(source.imageUrl)
      || firstImageUrl(source.imageContent);

    if (imageUrl) return imageUrl;
  }

  return '';
};

const getCategoryImageUrl = (category = {}) => firstImageUrl(
  category.imageContent
    || category.imageURL
    || category.imageUrl
);
const COURSE_SYMBOLS = ['</>', '☁', 'AI'];

export const getExamCourseDetailPath = (course = {}) => (
  course?.primaryId
    ? `/course-detail?primaryId=${encodeURIComponent(course.primaryId)}`
    : (course?.url || '/course-detail')
);

/**
 * Maps a live course/product record onto an exam catalog card. `source` keeps the original
 * record so the reader can hand it to `setSelectedProduct`, which is how the Default course
 * page opens the detail/purchase flow.
 */
const toCatalogCard = (course = {}, index = 0) => ({
  symbol: course.symbol || COURSE_SYMBOLS[index % COURSE_SYMBOLS.length],
  level: course.category?.name || course.categoryName || course.level || 'Proctored',
  duration: course.duration || course.examDuration || '',
  title: course.name || course.title || 'Certification exam',
  description: plainText(course.description) || 'Validate your skills with a proctored assessment and receive verified results.',
  count: course.totalQuestions || course.questionCount || '',
  categoryId: course.category?.primaryId ?? course.categoryId ?? null,
  categoryName: course.category?.name || course.categoryName || '',
  imageUrl: getProductImageUrl(course) || getCategoryImageUrl(course.category),
  url: getExamCourseDetailPath(course),
  source: course
});

const SORT_OPTIONS = ['Most popular', 'Newest', 'A to Z', 'Shortest duration'];

const sortCatalog = (cards, sort) => {
  const list = [...cards];
  if (sort === 'A to Z') return list.sort((a, b) => String(a.title).localeCompare(String(b.title)));
  if (sort === 'Newest') return list.reverse();
  if (sort === 'Shortest duration') {
    const minutes = (value) => parseInt(String(value).replace(/[^\d]/g, ''), 10) || Number.MAX_SAFE_INTEGER;
    return list.sort((a, b) => minutes(a.duration) - minutes(b.duration));
  }
  return list;
};

export const filterExamCatalogCards = (cards = [], query = '', selectedCategories = []) => {
  const term = query.trim().toLowerCase();
  return cards.filter((card) => {
    const matchesTerm = !term
      || String(card.title).toLowerCase().includes(term)
      || String(card.description).toLowerCase().includes(term)
      || String(card.categoryName).toLowerCase().includes(term);
    const matchesCategory = selectedCategories.length === 0
      || selectedCategories.some((category) => (
        String(card.categoryId) === String(category)
        || String(card.categoryName) === String(category)
      ));
    return matchesTerm && matchesCategory;
  });
};

/**
 * Inner-page hero. Carries the same furniture as the landing hero — the teal gradient plus
 * the four wave arcs — so every page reads as one system (the shared bundle applies the same
 * `.topbar` treatment across index/courses/course-details/about).
 */
const PageHero = ({ children, crumb, defaults, content, auth, onNavigate, compact = false }) => (
  <section className={`aae-page-hero${compact ? ' aae-auth-page-hero' : ''}`}>
    <div className="aae-wave w1" />
    <div className="aae-wave w2" />
    <div className="aae-wave w3" />
    <div className="aae-wave w4" />

    <ExamNav content={{ ...CHROME_DEFAULTS, ...content }} auth={auth} onNavigate={onNavigate} />
    <div className="aae-page-head">
      <div className="aae-crumb">
        <a {...examLinkProps('/', onNavigate)}>Home</a>
        <span>/</span>
        <strong>{crumb || defaults.crumb}</strong>
      </div>
      {children}
    </div>
  </section>
);

/**
 * Catalog body: search, category filter and sort all operate on the live product list.
 *
 * Category options come from the real product categories rather than a hardcoded list, so the
 * controls always reflect the company's own data. Selecting a card calls `onSelectCourse`,
 * which the reader wires to the Default template's own handler
 * (`dispatch(setSelectedProduct(product))` then navigate to `/course-detail?primaryId=...`) so the purchase
 * flow is entered exactly as it is from the Default course page.
 */
const ExamCatalog = ({ content, defaults, cards, categories, onSelectCourse, query, setQuery, selectedCategories, setSelectedCategories, loading, error, onRetry }) => {
  const [sort, setSort] = React.useState(SORT_OPTIONS[0]);

  const categoryOptions = visibleItems(categories)
    .map((category) => ({ id: category.primaryId ?? category.id ?? category.name, label: category.name || category.title }))
    .filter((option) => option.label);

  const term = query.trim().toLowerCase();
  const filtered = filterExamCatalogCards(cards, query, selectedCategories);
  const visible = sortCatalog(filtered, sort);

  const openCourse = (card) => (event) => {
    if (!onSelectCourse) return;
    event.preventDefault();
    onSelectCourse(card.source || card);
  };

  const resetAll = () => {
    setQuery('');
    setSelectedCategories([]);
    setSort(SORT_OPTIONS[0]);
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories((current) => current.some((item) => String(item) === String(categoryId))
      ? current.filter((item) => String(item) !== String(categoryId))
      : [...current, categoryId]);
  };

  const isFiltered = Boolean(term || selectedCategories.length);

  return (
    <section className="aae-section" id="exam-catalog">
      <div className="aae-wrap">
        <div className="aae-kicker">{pageValue(content, defaults, 'kicker')}</div>
        <h2 className="aae-title">{pageValue(content, defaults, 'heading')}</h2>
        <p className="aae-lead">{pageValue(content, defaults, 'lead')}</p>

        <div className="aae-catalog">
          <aside className="aae-filters" aria-label="Exam filters">
            <div className="aae-filter-head">
              <h3>Filters</h3>
              <button className="aae-reset" type="button" onClick={resetAll}>Reset all</button>
            </div>

            <div className="aae-filter-group">
              <h4>Search</h4>
              <label className="aae-field">
                <input
                  type="search"
                  value={query}
                  placeholder={pageValue(content, defaults, 'searchPlaceholder')}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            </div>

            {categoryOptions.length ? (
              <div className="aae-filter-group">
                <h4>Category</h4>
                {categoryOptions.map((option, index) => (
                  <label className="aae-check" key={`${option.label}-${index}`}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.some((item) => String(item) === String(option.id))}
                      onChange={() => toggleCategory(option.id)}
                    /> {option.label}
                  </label>
                ))}
              </div>
            ) : null}
          </aside>

          <div>
            <div className="aae-results-head">
              <strong>
                {`Showing ${visible.length} ${visible.length === 1 ? 'exam' : 'exams'}`}
              </strong>
              <select aria-label="Sort exams" value={sort} onChange={(event) => setSort(event.target.value)}>
                {SORT_OPTIONS.map((option) => <option key={option}>{option}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="aae-empty" role="status"><strong>Loading exams…</strong><span>Please wait while the catalog is loaded.</span></div>
            ) : error ? (
              <div className="aae-empty" role="alert"><strong>We could not load the exams.</strong><span>Please try again.</span><button className="aae-btn aae-btn-primary" type="button" onClick={onRetry}>Retry</button></div>
            ) : visible.length ? (
              <div className="aae-course-grid">
                {visible.map((card, index) => (
                  <a
                    className="aae-course-card"
                    href={card.url}
                    key={`${card.title}-${index}`}
                    onClick={openCourse(card)}
                  >
                    <div className={`aae-course-art ${COURSE_ART[index % COURSE_ART.length]}${card.imageUrl ? ' has-product-image' : ''}`}>
                      {!card.imageUrl ? <div className="aae-course-symbol">{card.symbol}</div> : null}
                      {card.imageUrl ? (
                        <img
                          className="aae-product-image"
                          src={card.imageUrl}
                          alt={card.title}
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.remove();
                          }}
                        />
                      ) : null}
                    </div>
                    <div className="aae-course-body">
                      <div className="aae-course-meta">
                        {card.level ? <span className="aae-pill">{card.level}</span> : null}
                        {card.duration ? <span className="aae-pill">{card.duration}</span> : null}
                      </div>
                      <h3>{card.title}</h3>
                      <p>{card.description}</p>
                      <div className="aae-course-foot">
                        <span>{card.count ? <><strong>{card.count}</strong> questions</> : null}</span>
                        <span>View exam →</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="aae-empty" role="status">
                <strong>{isFiltered ? 'No exams match your filters.' : 'No Exams Available'}</strong>
                <span>{isFiltered ? 'Try a different category or clear the search.' : 'New certification exams will appear here when they are published.'}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const categoryTileOptions = (categories = [], cards = []) => {
  const liveCategories = visibleItems(categories)
    .map((category) => ({
      icon: category.icon || String(category.name || category.title || 'EX').slice(0, 2).toUpperCase(),
      id: category.primaryId ?? category.id ?? category.name ?? category.title,
      title: category.name || category.title,
      meta: category.examCount || category.productCount || category.count ? `${category.examCount || category.productCount || category.count} exams` : '',
      url: category.url || '/course'
    }))
    .filter((category) => category.title);

  if (liveCategories.length) return liveCategories;

  const uniqueCardCategories = new Map();
  visibleItems(cards).filter((card) => card.source).forEach((card) => {
    const title = card.categoryName || card.level;
    if (title && !uniqueCardCategories.has(title)) {
      uniqueCardCategories.set(title, {
        icon: String(title).slice(0, 2).toUpperCase(),
        id: card.categoryId ?? title,
        title,
        meta: '',
        url: '/course'
      });
    }
  });
  return Array.from(uniqueCardCategories.values());
};

const CoursesPage = ({ content, defaults, cards, auth, categories, onSelectCourse, onNavigate, companyName, loading, error, onRetry }) => {
  const [query, setQuery] = React.useState('');
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  React.useEffect(() => {
    if (window.location.hash === '#exam-catalog') {
      window.requestAnimationFrame(() => document.getElementById('exam-catalog')?.scrollIntoView({ block: 'start' }));
    }
  }, []);
  const chooseCategory = (categoryId) => {
    setSelectedCategories([categoryId]);
    window.requestAnimationFrame(() => document.getElementById('exam-catalog')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  return (
  <>
    <PageHero content={content} defaults={defaults} auth={auth} onNavigate={onNavigate} crumb={pageValue(content, defaults, 'crumb')}>
      <div className="aae-eyebrow">{companyName ? `Explore ${companyName} certification exams` : pageValue(content, defaults, 'eyebrow')}</div>
      <h1>{pageValue(content, defaults, 'title')}</h1>
      <p>{pageValue(content, defaults, 'subtitle')}</p>
      <div className="aae-hero-tools">
        <label className="aae-searchbox" aria-label="Search exams">
          <span>⌕</span>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={pageValue(content, defaults, 'searchPlaceholder')} />
          <button type="button" onClick={() => document.getElementById('exam-catalog')?.scrollIntoView({ behavior: 'smooth' })}>{pageValue(content, defaults, 'searchButtonLabel')}</button>
        </label>
        <div className="aae-hero-count">
          <strong>{loading ? '—' : cards.length}</strong>
          <span>{pageValue(content, defaults, 'countLabel')}</span>
        </div>
      </div>
    </PageHero>

    <ExamCatalog
      content={content}
      defaults={defaults}
      cards={cards}
      categories={categories}
      onSelectCourse={onSelectCourse}
      query={query}
      setQuery={setQuery}
      selectedCategories={selectedCategories}
      setSelectedCategories={setSelectedCategories}
      loading={loading}
      error={error}
      onRetry={onRetry}
    />

    {categoryTileOptions(categories, cards).length ? (
      <section className="aae-categories" id="categories">
        <div className="aae-wrap">
          <div className="aae-kicker">{pageValue(content, defaults, 'categoriesKicker')}</div>
          <h2 className="aae-title">{pageValue(content, defaults, 'categoriesTitle')}</h2>
          <div className="aae-category-grid">
            {categoryTileOptions(categories, cards).map((category, index) => (
              <button className="aae-category" type="button" onClick={() => chooseCategory(category.id)} key={`${category.title}-${index}`}>
                <i>{category.icon}</i>
                <div>
                  <strong>{category.title}</strong>
                  {category.meta ? <span>{category.meta}</span> : null}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    ) : null}
  </>
  );
};

/** Delivery labels, matching CourseInfo's getDeliveryTypeLabels. */
const deliveryLabel = (course = {}) => {
  if (course.deliveryType === 'BRICK_MORTAR') return 'In person';
  if (course.deliveryType === 'VIRTUAL') {
    return course.deliverySubType === 'INSTRUCTOR_LED' ? 'Instructor-led online' : 'Self-paced online';
  }
  return course.deliveryType ? String(course.deliveryType).replace(/_/g, ' ').toLowerCase() : '';
};

const validityLabel = (days) => {
  const value = Number(days);
  if (!value || Number.isNaN(value)) return 'No expiration';
  if (value % 365 === 0) return `${value / 365} year${value / 365 === 1 ? '' : 's'}`;
  return `${value} days`;
};

export const ExamPrice = ({ course = {}, company }) => {
  const amount = course.price?.priceAmt;
  if (amount === undefined || amount === null || amount === '') return 'Price unavailable';
  if (Number(amount) === 0) return 'Free';
  return <FormatByOperatingLocation value={amount} type="CURRENCY" company={company} />;
};

export const examMetadata = (curriculums = []) => (
  (Array.isArray(curriculums) ? curriculums : [])
    .flatMap((curriculum) => Array.isArray(curriculum?.examsList) ? curriculum.examsList : [])
    .filter((exam) => exam && String(exam.subType || exam.type || '').toUpperCase() !== 'QUIZ')
    .sort((a, b) => (a.curriculumSerialNumber || 0) - (b.curriculumSerialNumber || 0))
);

/**
 * Builds curriculum-level blueprint sections from non-Quiz exams only.
 * Exam pages must not expose course chapter counts, labels, or rows even when
 * the curriculum payload contains chapter data.
 *
 * @param {Array<object>} curriculums curriculum records from the live course
 * @returns {Array<object>} curriculum sections suitable for the blueprint UI
 */
export const curriculumModules = (curriculums = []) => (
  (Array.isArray(curriculums) ? curriculums : [])
    .filter(Boolean)
    .map((curriculum) => ({
      curriculum,
      exams: (Array.isArray(curriculum.examsList) ? curriculum.examsList : [])
        .filter((exam) => exam && String(exam.subType || exam.type || '').toUpperCase() !== 'QUIZ')
    }))
    .filter(({ exams }) => exams.length)
    .map(({ curriculum, exams }, index) => {
      const entries = exams
        .map((item, position) => ({ ...item, kind: 'exam', position: position + 1 }))
        .sort((a, b) => (a.curriculumSerialNumber || 0) - (b.curriculumSerialNumber || 0));

      const minutes = entries.reduce((sum, entry) => {
        if (entry?.contentList?.length) {
          return sum + entry.contentList.reduce(
            (contentSum, content) => contentSum + getEffectiveDurationInMinutes(content),
            0
          );
        }
        return sum + ((Number(entry?.durationInHours ?? entry?.duration) || 0) * 60);
      }, 0);
      const hours = minutes / 60;
      const rounded = Number.isInteger(hours) ? hours : Number(hours.toFixed(1));

      return {
        number: String(index + 1).padStart(2, '0'),
        title: curriculum.name || curriculum.title || `Curriculum ${index + 1}`,
        summary: `${exams.length} ${exams.length === 1 ? 'exam' : 'exams'}`,
        meta: rounded ? `${rounded} ${rounded === 1 ? 'hour' : 'hours'}` : '',
        lessons: entries.map((entry) => ({
          title: entry.name || entry.title || 'Exam',
          detail: plainText(entry.description, 120),
          meta: 'Exam',
          isProject: true
        }))
      };
    })
);

const namedValues = (values = []) => (Array.isArray(values) ? values : [])
  .map((value) => typeof value === 'string' ? value : value?.name || value?.label || value?.value)
  .filter(Boolean);

const examDurationMinutes = (exam = {}) => {
  const value = Number(exam.duration ?? exam.durationInMinutes ?? 0) || 0;
  const unit = String(exam.durationType || '').toUpperCase();
  if (unit.includes('DAY')) return value * 1440;
  if (unit.includes('HOUR')) return value * 60;
  return value;
};

const durationLabel = (minutes) => {
  if (!minutes) return '—';
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return minutes > 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
};

const passMarkLabel = (exam = {}) => {
  if (exam.passMark != null && exam.passMark !== '') return `${Number(exam.passMark)}%`;
  if (exam.requiredMarks != null && exam.requiredMarks !== '') return `${Number(exam.requiredMarks)} marks`;
  return '';
};

export const examBlueprint = (exams = []) => exams.map((exam, index) => {
  const categories = namedValues(exam.applicableCategories);
  const tags = namedValues(exam.listOfTags);
  const questions = Number(exam.noOfQuestions ?? exam.questionCount ?? 0) || 0;
  const passMark = passMarkLabel(exam);
  const metadataRows = [
    categories.length ? { title: 'Categories', detail: categories.join(', '), meta: '' } : null,
    tags.length ? { title: 'Tags', detail: tags.join(', '), meta: '' } : null,
    passMark ? { title: 'Pass mark', detail: 'Minimum score required to pass this section.', meta: passMark } : null
  ].filter(Boolean);
  return {
    number: String(index + 1).padStart(2, '0'),
    title: exam.name || exam.title || `Exam section ${index + 1}`,
    summary: plainText(exam.description, 180) || 'Assessment details configured by the examiner.',
    meta: [questions ? `${questions} questions` : '', examDurationMinutes(exam) ? durationLabel(examDurationMinutes(exam)) : ''].filter(Boolean).join(' · '),
    lessons: metadataRows
  };
});

export const examAssessmentItems = (exams = []) => exams.map((exam, index) => ({
  title: exam.name || exam.title || `Exam section ${index + 1}`,
  description: plainText(exam.description, 220),
  tags: [...namedValues(exam.applicableCategories), ...namedValues(exam.listOfTags)]
}));

export const examSummary = (exams = []) => {
  const questions = exams.reduce((sum, exam) => sum + (Number(exam.noOfQuestions ?? exam.questionCount ?? 0) || 0), 0);
  const minutes = exams.reduce((sum, exam) => sum + examDurationMinutes(exam), 0);
  const marks = [...new Set(exams.map(passMarkLabel).filter(Boolean))];
  return [
    { value: String(exams.length), label: exams.length === 1 ? 'Section' : 'Sections' },
    { value: questions ? String(questions) : '—', label: 'Questions' },
    { value: durationLabel(minutes), label: 'Duration' },
    { value: marks.length === 1 ? marks[0] : marks.length > 1 ? 'Varies' : '—', label: 'Pass mark' }
  ];
};

/** Human label for a schedule option, tolerant of the fields the backend supplies. */
const scheduleLabel = (schedule = {}, index = 0) => {
  const effectiveDate = schedule.period?.effectiveDate || schedule.effectiveDate || {};
  const start = effectiveDate.effectiveStartDate || effectiveDate.localStartDate || schedule.period?.effectiveStartDate || schedule.effectiveStartDate;
  const end = effectiveDate.effectiveEndDate || effectiveDate.localEndDate || schedule.period?.effectiveEndDate || schedule.effectiveEndDate;
  const timeZone = effectiveDate.timeZone || schedule.period?.timeZone || schedule.timeZone;
  const asDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString([], { dateStyle: 'medium', timeZone: timeZone || undefined });
  };
  const asTime = (value) => {
    if (!value) return '';
    const [hours, minutes] = String(value).split(':');
    const date = new Date(2020, 0, 1, Number(hours), Number(minutes));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };
  const dateRange = [asDate(start), asDate(end)].filter(Boolean).join(' – ');
  const timeRange = [asTime(schedule.schedule?.startTime || schedule.startTime), asTime(schedule.schedule?.endTime || schedule.endTime)].filter(Boolean).join(' – ');
  return [dateRange, timeRange].filter(Boolean).join(' · ') || schedule.schedule?.scheduleCode || schedule.scheduleCode || `Schedule ${index + 1}`;
};

const scheduleCode = (schedule = {}, index = 0) => schedule.schedule?.scheduleCode || schedule.scheduleCode || schedule.code || `Schedule ${index + 1}`;

const ownerRoles = (owner = {}) => [owner.userRole, owner.role, ...(owner.userRoles || []), ...(owner.roles || [])]
  .map((role) => typeof role === 'string' ? role : role?.name || role?.roleName || role?.type)
  .filter(Boolean)
  .map((role) => String(role).toUpperCase());

export const scheduleInstructor = (schedule = {}) => {
  const owners = Array.isArray(schedule.owners) ? schedule.owners.filter(Boolean) : [];
  const owner = owners.find((candidate) => ownerRoles(candidate).some((role) => role.includes('INSTRUCTOR'))) || owners[0];
  if (!owner) return null;
  const name = owner.name || owner.displayName || [owner.firstName, owner.lastName].filter(Boolean).join(' ') || owner.email || 'Instructor';
  return { ...owner, displayName: name, initials: name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() };
};

const interviewTimeLabel = (time) => {
  if (!time) return '';
  const [hours, minutes] = String(time).split(':');
  const date = new Date(2020, 0, 1, Number(hours), Number(minutes));
  return Number.isNaN(date.getTime()) ? String(time) : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const interviewTimeRangeLabel = (slot = {}) => (
  `${interviewTimeLabel(slot.startTime)} - ${interviewTimeLabel(slot.endTime)}`
);

const interviewDateLabel = (date) => {
  if (!date) return '';
  const parsed = new Date(`${date}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const shiftInterviewDate = (date, days) => {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  parsed.setDate(parsed.getDate() + days);
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

const interviewerInitials = (name = '') => String(name).split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'IN';

const interviewerSkills = (product = {}) => {
  const attribute = (product.dynamicAttributes || []).find(
    (item) => String(item?.name || '').toLowerCase() === 'interviewer_skills'
  );
  return String(attribute?.value || '').split(',').map((skill) => skill.trim()).filter(Boolean);
};

const InterviewAvailabilityPicker = ({ purchase }) => {
  const groups = (purchase?.interviewSlots || []).filter((slot) => (
    String(slot.status?.name || slot.status || '').toUpperCase() === 'OPEN'
  )).reduce((result, slot) => {
    const interviewerId = slot.assignedProductId || slot.suggestedProduct?.primaryId || 'unknown';
    if (!result[interviewerId]) {
      result[interviewerId] = {
        name: slot.suggestedProduct?.name || 'Interviewer',
        skills: interviewerSkills(slot.suggestedProduct),
        slots: []
      };
    }
    result[interviewerId].slots.push(slot);
    return result;
  }, {});
  const interviewerIds = Object.keys(groups);
  const availableSkills = [...new Set(interviewerIds.flatMap((id) => groups[id].skills || []))];
  const [activeSkill, setActiveSkill] = React.useState('all');
  const visibleInterviewerIds = activeSkill === 'all'
    ? interviewerIds
    : interviewerIds.filter((id) => (groups[id].skills || []).includes(activeSkill));
  const availableSkillsKey = availableSkills.join(',');
  const interviewerKey = visibleInterviewerIds.join(',');
  const [selectedInterviewerId, setSelectedInterviewerId] = React.useState('');
  const [selectedPeriod, setSelectedPeriod] = React.useState('morning');

  React.useEffect(() => {
    if (activeSkill !== 'all' && !availableSkillsKey.split(',').includes(activeSkill)) {
      setActiveSkill('all');
    }
  }, [activeSkill, availableSkillsKey]);

  React.useEffect(() => {
    const availableInterviewerIds = interviewerKey ? interviewerKey.split(',') : [];
    if (!availableInterviewerIds.includes(selectedInterviewerId)) {
      setSelectedInterviewerId(availableInterviewerIds[0] || '');
      purchase.onSelectInterviewSlot?.(null);
    }
  }, [interviewerKey, selectedInterviewerId, purchase]);

  const selectedGroup = groups[selectedInterviewerId];
  const periodSlots = selectedGroup?.slots.filter((slot) => {
    const hour = Number(String(slot.startTime || '').split(':')[0]);
    if (selectedPeriod === 'morning') return hour < 12;
    if (selectedPeriod === 'afternoon') return hour >= 12 && hour < 17;
    return hour >= 17;
  }) || [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="aae-interview-picker">
      <div className="aae-booking-step">
        <div className="aae-booking-step-label"><span>1. Choose a day</span><strong>{interviewDateLabel(purchase.interviewDate)}</strong></div>
        <div className="aae-date-nav">
          <button type="button" aria-label="Previous day" disabled={!purchase.interviewDate || purchase.interviewDate <= today} onClick={() => purchase.onInterviewDateChange?.(shiftInterviewDate(purchase.interviewDate, -1))}>‹</button>
          <input id="aae-interview-date" type="date" min={today} value={purchase.interviewDate || ''} onChange={(event) => purchase.onInterviewDateChange?.(event.target.value)} />
          <button type="button" aria-label="Next day" disabled={!purchase.interviewDate} onClick={() => purchase.onInterviewDateChange?.(shiftInterviewDate(purchase.interviewDate, 1))}>›</button>
        </div>
        <span className="aae-date-caption">Times are shown in your academy timezone</span>
      </div>

      {purchase.interviewSlotsLoading ? <span>Loading availability...</span> : null}
      {!purchase.interviewSlotsLoading && !interviewerIds.length ? <div className="aae-no-times">No interviewers have open times on this day. Try another date.</div> : null}

      {interviewerIds.length ? (
        <>
          <div className="aae-booking-step">
            <div className="aae-booking-step-label"><span>2. Choose an interviewer</span><strong>{visibleInterviewerIds.length} available</strong></div>
            {availableSkills.length ? (
              <div className="aae-interviewer-skill-filter" aria-label="Filter interviewers by skill">
                <button type="button" className={activeSkill === 'all' ? 'is-selected' : ''} onClick={() => { setActiveSkill('all'); purchase.onSelectInterviewSlot?.(null); }}>All skills</button>
                {availableSkills.map((skill) => (
                  <button key={skill} type="button" className={activeSkill === skill ? 'is-selected' : ''} onClick={() => { setActiveSkill(skill); purchase.onSelectInterviewSlot?.(null); }}>{skill}</button>
                ))}
              </div>
            ) : null}
            <div className="aae-interviewer-list">
              {visibleInterviewerIds.map((interviewerId) => {
                const group = groups[interviewerId];
                return (
                  <button key={interviewerId} type="button" className={`aae-interviewer-option${selectedInterviewerId === interviewerId ? ' is-selected' : ''}`} onClick={() => { setSelectedInterviewerId(interviewerId); purchase.onSelectInterviewSlot?.(null); }}>
                    <span className="aae-interviewer-avatar">{interviewerInitials(group.name)}</span>
                    <span className="aae-interviewer-copy"><strong>{group.name}</strong><span>{group.slots.length} times available</span>{group.skills.length ? <span className="aae-interviewer-skills">{group.skills.slice(0, 3).map((skill) => <em key={skill}>{skill}</em>)}</span> : <span className="aae-interviewer-skills-empty">General interview coverage</span>}</span>
                  </button>
                );
              })}
            </div>
            {!visibleInterviewerIds.length ? <div className="aae-no-times">No interviewer with this skill has open times on this day.</div> : null}
          </div>

          <div className="aae-booking-step">
            <div className="aae-booking-step-label"><span>3. Choose a time</span><strong>{periodSlots.length} open</strong></div>
            <div className="aae-period-tabs">
              {['morning', 'afternoon', 'evening'].map((period) => (
                <button key={period} type="button" className={`aae-period-tab${selectedPeriod === period ? ' is-selected' : ''}`} onClick={() => setSelectedPeriod(period)}>{period[0].toUpperCase() + period.slice(1)}</button>
              ))}
            </div>
            {periodSlots.length ? (
              <div className="aae-time-grid">
                {periodSlots.map((slot) => {
                  const key = [slot.assignedProductId, slot.date, slot.startTime, slot.endTime].join('-');
                  const selected = purchase.selectedInterviewSlot && [purchase.selectedInterviewSlot.assignedProductId, purchase.selectedInterviewSlot.date, purchase.selectedInterviewSlot.startTime, purchase.selectedInterviewSlot.endTime].join('-') === key;
                  return <button key={key} type="button" className={`aae-time-option${selected ? ' is-selected' : ''}`} onClick={() => purchase.onSelectInterviewSlot?.(slot)}>{interviewTimeRangeLabel(slot)}</button>;
                })}
              </div>
            ) : <div className="aae-no-times">No open times in this period. Try another period.</div>}
          </div>

          {purchase.selectedInterviewSlot ? (
            <div className="aae-selection-summary">
              <div><strong>{purchase.selectedInterviewSlot.suggestedProduct?.name || 'Interviewer'}</strong><span>{interviewDateLabel(purchase.selectedInterviewSlot.date)} at {interviewTimeRangeLabel(purchase.selectedInterviewSlot)}</span></div>
              <span className="aae-selection-check">✓</span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const interviewSkills = (course = {}) => {
  const skillAttribute = (course.optionalAttributes || []).find((attribute) => String(attribute?.name || '').toLowerCase() === 'skill');
  const values = skillAttribute?.values || [];
  return values.map((value) => typeof value === 'string' ? value : value?.value).filter(Boolean);
};

const InterviewCourseDetailPage = ({ content, defaults, auth, purchase, onNavigate }) => {
  const skills = interviewSkills(purchase?.course);
  const focusSkills = skills.length ? skills : ['Technical discussion', 'Problem solving', 'Communication'];
  const selectedSlot = purchase?.selectedInterviewSlot;

  return (
    <div className="aae-interview-page">
      <div className="aae-interview-nav">
        <ExamNav content={{ ...CHROME_DEFAULTS, ...content }} auth={auth} onNavigate={onNavigate} />
      </div>
      <main className="aae-interview-main">
        <div className="aae-interview-crumb">
          <a {...examLinkProps('/', onNavigate)}>Home</a><span>/</span><strong>{pageValue(content, defaults, 'title')}</strong>
        </div>

        <section className="aae-interview-intro">
          <div>
            <div className="aae-interview-eyebrow"><i /> Live technical interview</div>
            <h1>{pageValue(content, defaults, 'title')}</h1>
            <p>{pageValue(content, defaults, 'subtitle')}</p>
            <div className="aae-interview-meta">
              <span><Clock3 /> 60 minutes</span>
              <span><Video /> Online video call</span>
              <span><Sparkles /> Human-led evaluation</span>
            </div>
          </div>
          <div className="aae-interview-focus">
            <span className="aae-interview-focus-label">Interview focus</span>
            <h3>What you will discuss</h3>
            <div className="aae-skill-list">{focusSkills.map((skill) => <span key={skill}>{skill}</span>)}</div>
          </div>
        </section>

        <section className="aae-interview-workspace">
          <div>
            <div className="aae-interview-benefits">
              <h2>Make the conversation count.</h2>
              <p>Choose a time that works for you. We will match you with an available interviewer and confirm the session instantly.</p>
              {(pageValue(content, defaults, 'previewList') || ['A focused technical conversation', 'Live feedback from an interviewer', 'A verified result after your session']).slice(0, 3).map((item, index) => (
                <div className="aae-interview-benefit" key={`${item}-${index}`}><Check size={16} strokeWidth={3} /><span>{item}</span></div>
              ))}
            </div>
            <div className="aae-interview-info">
              <article><CalendarDays /><strong>Pick your day</strong><span>See real availability before you commit.</span></article>
              <article><MonitorPlay /><strong>Join online</strong><span>Your meeting details are shared after booking.</span></article>
              <article><Check /><strong>Get evaluated</strong><span>Complete the interview with a human expert.</span></article>
            </div>
          </div>

          <aside className="aae-interview-booking">
            <div className="aae-interview-booking-head">
              <h2>Reserve your interview</h2>
              <div className="aae-interview-price">{pageValue(content, defaults, 'priceLabel')}<small>per session</small></div>
            </div>
            <div className="aae-interview-progress"><span className="is-active" /><span className={purchase?.interviewDate ? 'is-active' : ''} /><span className={selectedSlot ? 'is-active' : ''} /></div>
            <InterviewAvailabilityPicker purchase={purchase} />
            {purchase?.register ? (
              <button className="aae-enroll" type="button" id="enroll-now-button" onClick={purchase.register} disabled={purchase.isProcessing || !purchase.course || !selectedSlot}>
                {purchase.isProcessing ? 'Processing...' : 'Book interview'}
              </button>
            ) : null}
            {purchase?.alert?.open ? <div className={`aae-alert is-${purchase.alert.severity}`} role="status">{purchase.alert.message}</div> : null}
          </aside>
        </section>
      </main>
    </div>
  );
};

const CourseDetailLoadingPage = () => (
  <div className="aae-course-detail-loader" role="status" aria-label="Loading course details">
    <div className="aae-course-detail-skeleton-nav" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
    <div className="aae-course-detail-skeleton-content" aria-hidden="true">
      <div className="aae-course-detail-skeleton-crumb" />
      <div className="aae-course-detail-skeleton-eyebrow" />
      <div className="aae-course-detail-skeleton-title" />
      <div className="aae-course-detail-skeleton-subtitle" />
      <div className="aae-course-detail-skeleton-subtitle short" />
      <div className="aae-course-detail-skeleton-grid">
        <div className="aae-course-detail-skeleton-panel">
          <div className="aae-course-detail-skeleton-line heading" />
          <div className="aae-course-detail-skeleton-line wide" />
          <div className="aae-course-detail-skeleton-line wide" />
          <div className="aae-course-detail-skeleton-line medium" />
          <div className="aae-course-detail-skeleton-block" />
        </div>
        <div className="aae-course-detail-skeleton-panel side">
          <div className="aae-course-detail-skeleton-line heading" />
          <div className="aae-course-detail-skeleton-line price" />
          <div className="aae-course-detail-skeleton-line wide" />
          <div className="aae-course-detail-skeleton-line medium" />
          <div className="aae-course-detail-skeleton-line button" />
        </div>
      </div>
    </div>
  </div>
);

const COURSE_DETAIL_STATUS_CONTENT = {};
const COURSE_DETAIL_STATUS_DEFAULTS = { crumb: 'Course details' };

const CourseDetailStatusPage = ({ auth, loading, redirecting, onNavigate }) => {
  const eyebrow = redirecting ? 'Exam catalogue' : loading ? 'Loading exam' : 'Exam unavailable';
  const title = redirecting
    ? 'Choose an exam to continue'
    : loading
      ? 'Loading exam details...'
      : 'Exam details are unavailable';
  const subtitle = redirecting
    ? 'Returning you to the exam catalogue so you can select an available course.'
    : loading
      ? 'Please wait while we retrieve the selected exam details.'
      : 'The requested exam could not be found. Return to the exam catalogue and choose an available course.';

  return (
    <PageHero
      content={COURSE_DETAIL_STATUS_CONTENT}
      defaults={COURSE_DETAIL_STATUS_DEFAULTS}
      crumb="Course details"
      auth={auth}
      onNavigate={onNavigate}
    >
      <div className="aae-eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      <p>{subtitle}</p>
      <div className="aae-detail-grid">
        <div className="aae-panel" role="status">
          <strong>{redirecting ? 'Returning to the exam catalogue...' : loading ? 'Loading your exam...' : 'This exam is not available.'}</strong>
          <p>{redirecting ? 'A course must be selected before exam details can be opened.' : loading ? 'Your selected course will appear here when it is ready.' : 'Please return to the exam catalogue and select the course again.'}</p>
        </div>
      </div>
    </PageHero>
  );
};

const CourseDetailPage = ({ content, defaults, auth, modules, assessmentItems, summary, instructor, metadataState, purchase, onNavigate }) => {
  if (purchase?.isInterviewProduct) {
    return <InterviewCourseDetailPage content={content} defaults={defaults} auth={auth} purchase={purchase} onNavigate={onNavigate} />;
  }

  return (
  <>
    <PageHero content={content} defaults={defaults} auth={auth} onNavigate={onNavigate} crumb={pageValue(content, defaults, 'crumb')}>
      <div className="aae-detail-grid">
        <div>
          <div className="aae-eyebrow">{pageValue(content, defaults, 'eyebrow')}</div>
          <h1>{pageValue(content, defaults, 'title')}</h1>
          <p>{pageValue(content, defaults, 'subtitle')}</p>
          <div className="aae-detail-pills">
            {(pageValue(content, defaults, 'pills') || []).map((pill, index) => (
              <span key={`${pill}-${index}`}>{pill}</span>
            ))}
          </div>
          {instructor ? <div className="aae-instructor">
            <div className="aae-avatar">{instructor.initials}</div>
            <div><strong>{instructor.displayName}</strong><span>Instructor</span></div>
          </div> : null}
        </div>

        <aside className="aae-preview-card">
          <h3>{pageValue(content, defaults, 'previewTitle')}</h3>
          <div className="aae-price">{pageValue(content, defaults, 'priceLabel')}</div>

          {/* Schedule picker only appears for courses that require one, matching the Default
              page, which blocks enrolment until a slot is chosen. */}
          {purchase?.isInterviewProduct ? (
            <InterviewAvailabilityPicker purchase={purchase} />
          ) : purchase?.isScheduleRequired && purchase.schedules?.length ? (
            <div className="aae-field aae-schedule-field">
              <div id="aae-schedule-label">Choose your schedule</div>
              <div className="aae-schedule-options" role="radiogroup" aria-labelledby="aae-schedule-label">
                {purchase.schedules.map((schedule, index) => (
                  <label className="aae-schedule-option" key={schedule.primaryId ?? index}>
                    <input
                      type="radio"
                      name="aae-schedule"
                      checked={purchase.selectedSchedule === schedule || (schedule.primaryId != null && String(purchase.selectedSchedule?.primaryId) === String(schedule.primaryId))}
                      onChange={() => purchase.onSelectSchedule?.(schedule)}
                    />
                    <span>
                      <strong>{scheduleLabel(schedule, index)}</strong>
                      <span>{scheduleCode(schedule, index)}{scheduleInstructor(schedule) ? ` · ${scheduleInstructor(schedule).displayName}` : ''}</span>
                      {(schedule.period?.effectiveDate?.timeZone || schedule.effectiveDate?.timeZone) ? <span>{schedule.period?.effectiveDate?.timeZone || schedule.effectiveDate?.timeZone}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : purchase?.isScheduleRequired ? <div className="aae-no-times" role="status">No exam schedules are currently available.</div> : null}

          {purchase?.register ? (
            <button
              className="aae-enroll"
              type="button"
              id="enroll-now-button"
              onClick={purchase.register}
              disabled={purchase.isProcessing || !purchase.course || (purchase.isInterviewProduct && !purchase.selectedInterviewSlot) || (purchase.isScheduleRequired && !purchase.selectedSchedule)}
            >
              {purchase.isProcessing ? 'Processing...' : purchase.isInterviewProduct ? 'Book Interview' : pageValue(content, defaults, 'enrollLabel')}
            </button>
          ) : (
            <a className="aae-enroll" href="#register">{pageValue(content, defaults, 'enrollLabel')}</a>
          )}

          {purchase?.alert?.open ? (
            <div className={`aae-alert is-${purchase.alert.severity}`} role="status">
              {purchase.alert.message}
            </div>
          ) : null}

          <div className="aae-preview-list">
            {(pageValue(content, defaults, 'previewList') || []).map((item, index) => (
              <span key={`${item}-${index}`}>{item}</span>
            ))}
          </div>
        </aside>
      </div>
    </PageHero>

    <section className="aae-section">
      <div className="aae-wrap aae-detail-grid">
        <div>
          <article className="aae-panel">
            <h2>{pageValue(content, defaults, 'overviewTitle')}</h2>
            <p>{pageValue(content, defaults, 'overview')}</p>
            <h2 style={{ marginTop: 28 }}>{pageValue(content, defaults, 'learnTitle')}</h2>
            <div className="aae-learn-grid">
              {(assessmentItems === undefined ? (pageValue(content, defaults, 'learnItems') || []) : assessmentItems).map((item, index) => (
                <div className="aae-learn-item" key={`${typeof item === 'string' ? item : item.title}-${index}`}>
                  <i>✓</i>
                  <span>{typeof item === 'string' ? item : <><strong>{item.title}</strong>{item.description ? <span>{item.description}</span> : null}{item.tags?.length ? <small>{item.tags.join(' · ')}</small> : null}</>}</span>
                </div>
              ))}
              {assessmentItems !== undefined && !assessmentItems.length && !metadataState?.loading ? <div className="aae-empty"><strong>Assessment details are not configured.</strong><span>Exam metadata will appear here when it is published.</span></div> : null}
            </div>
          </article>

          <article className="aae-panel" id="blueprint">
            <div className="aae-kicker">{pageValue(content, defaults, 'curriculumKicker')}</div>
            <h2>{pageValue(content, defaults, 'curriculumTitle')}</h2>
            <p>{pageValue(content, defaults, 'curriculumMeta')}</p>

            <div className="aae-curriculum-summary">
              {visibleItems(summary === undefined ? pageValue(content, defaults, 'summary') : summary).map((item, index) => (
                <div key={`${item.label}-${index}`}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            {metadataState?.loading ? <div className="aae-empty" role="status"><strong>Loading exam blueprint…</strong></div> : null}
            {metadataState?.error ? <div className="aae-empty" role="alert"><strong>Exam blueprint could not be loaded.</strong><span>Please refresh the page to try again.</span></div> : null}
            {!metadataState?.loading && !metadataState?.error && modules !== undefined && !modules.length ? <div className="aae-empty"><strong>Exam blueprint is not configured.</strong><span>No exam sections have been published for this exam.</span></div> : null}
            {visibleItems(modules === undefined ? pageValue(content, defaults, 'modules') : modules).map((module, index) => (
              <details className="aae-module" key={`${module.title}-${index}`} open={index === 0}>
                <summary>
                  <span className="aae-module-number">{module.number}</span>
                  <span className="aae-module-title">
                    <strong>{module.title}</strong>
                    <small>{module.summary}</small>
                  </span>
                  <span className="aae-module-meta">{module.meta}</span>
                </summary>
                <div className="aae-lessons">
                  {(module.lessons || []).map((lesson, lessonIndex) => (
                    <div className={`aae-lesson-row${lesson.isProject ? ' is-project' : ''}`} key={`${lesson.title}-${lessonIndex}`}>
                      <i>{lesson.isProject ? '✓' : '▶'}</i>
                      <div>
                        <strong>{lesson.title}</strong>
                        <small>{lesson.detail}</small>
                      </div>
                      <span>{lesson.meta}</span>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </article>

          {instructor ? <article className="aae-panel">
            <h2>{pageValue(content, defaults, 'examinerTitle')}</h2>
            <div className="aae-instructor" style={{ marginTop: 8 }}>
              <div className="aae-avatar" style={{ background: '#173f3d', color: '#d8ff91' }}>
                {instructor.initials}
              </div>
              <div>
                <strong style={{ color: '#171918' }}>{instructor.displayName}</strong>
                <span style={{ color: '#68706d' }}>Instructor</span>
              </div>
            </div>
          </article> : null}
        </div>

        <aside>
          <div className="aae-side-card" id="register">
            <h3>{pageValue(content, defaults, 'sideTitle')}</h3>
            <div className="aae-side-list">
              {visibleItems(pageValue(content, defaults, 'sideList')).map((item, index) => (
                <div key={`${item.label}-${index}`}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
          <div className="aae-side-card is-certificate">
            <div className="aae-certificate-badge">★</div>
            <h3>{pageValue(content, defaults, 'certificateTitle')}</h3>
            <p>{pageValue(content, defaults, 'certificateBody')}</p>
          </div>
          <div className="aae-side-card">
            <h3>{pageValue(content, defaults, 'helpTitle')}</h3>
            <p style={{ color: '#68706d', lineHeight: 1.65, fontSize: 14 }}>{pageValue(content, defaults, 'helpBody')}</p>
            <a className="aae-enroll" style={{ marginTop: 16 }} {...examLinkProps(pageValue(content, defaults, 'helpUrl'), onNavigate)}>
              {pageValue(content, defaults, 'helpLabel')}
            </a>
          </div>
        </aside>
      </div>
    </section>
  </>
);

};

const AboutPage = ({ content, defaults, imageUrl, auth, onNavigate }) => (
  <>
    <PageHero content={content} defaults={defaults} auth={auth} onNavigate={onNavigate} crumb={pageValue(content, defaults, 'crumb')}>
      <div className="aae-about-hero-grid">
        <div>
          <div className="aae-eyebrow">{pageValue(content, defaults, 'eyebrow')}</div>
          <h1>{pageValue(content, defaults, 'title')}</h1>
          <p>{pageValue(content, defaults, 'subtitle')}</p>
          <div className="aae-about-actions">
            <a className="aae-btn aae-btn-primary" {...examLinkProps(pageValue(content, defaults, 'primaryUrl'), onNavigate)}>
              {pageValue(content, defaults, 'primaryLabel')}
            </a>
            <a className="aae-btn aae-btn-secondary" {...examLinkProps(pageValue(content, defaults, 'secondaryUrl'), onNavigate)}>
              {pageValue(content, defaults, 'secondaryLabel')}
            </a>
          </div>
        </div>

        {/* Same lime orbit ring + ellipse-clipped photo as the landing hero, matching the
            shared bundle's `.about-visual` / `.about-photo-wrap` treatment. */}
        <div className="aae-visual aae-about-visual">
          <div className="aae-orbit" />
          <img
            className="aae-student-photo"
            src={imageUrl}
            alt={pageValue(content, defaults, 'imageAlt') || 'Candidate sitting a proctored online exam'}
          />
          <div className="aae-about-float one">
            <strong>{pageValue(content, defaults, 'floatOneValue')}</strong>
            <span>{pageValue(content, defaults, 'floatOneLabel')}</span>
          </div>
          <div className="aae-about-float two">
            <strong>{pageValue(content, defaults, 'floatTwoValue')}</strong>
            <span>{pageValue(content, defaults, 'floatTwoLabel')}</span>
          </div>
        </div>
      </div>
    </PageHero>

    <section className="aae-section">
      <div className="aae-wrap">
        <div className="aae-story-split">
          <div>
            <div className="aae-kicker">{pageValue(content, defaults, 'storyKicker')}</div>
            <h2 className="aae-title">{pageValue(content, defaults, 'storyTitle')}</h2>
          </div>
          <div className="aae-story-copy">
            {(pageValue(content, defaults, 'storyParagraphs') || []).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="aae-section" style={{ paddingTop: 0 }}>
      <div className="aae-wrap aae-purpose-grid">
        {visibleItems(pageValue(content, defaults, 'purpose')).map((card, index) => (
          <article className={`aae-purpose-card ${index === 1 ? 'is-lime' : 'is-dark'}`} key={`${card.title}-${index}`}>
            <div className="aae-purpose-icon">{card.icon}</div>
            <span>{card.label}</span>
            <h2>{card.title}</h2>
            <p>{card.body}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="aae-section" id="approach" style={{ background: '#fff' }}>
      <div className="aae-wrap">
        <div className="aae-kicker">{pageValue(content, defaults, 'approachKicker')}</div>
        <h2 className="aae-title">{pageValue(content, defaults, 'approachTitle')}</h2>
        <p className="aae-lead">{pageValue(content, defaults, 'approachLead')}</p>
        <div className="aae-approach-grid">
          {visibleItems(pageValue(content, defaults, 'approach')).map((step, index) => (
            <article className="aae-approach-card" key={`${step.title}-${index}`}>
              <span>{step.number}</span>
              <i>{step.icon}</i>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="aae-section">
      <div className="aae-wrap aae-values-layout">
        <div>
          <div className="aae-kicker">{pageValue(content, defaults, 'valuesKicker')}</div>
          <h2 className="aae-title">{pageValue(content, defaults, 'valuesTitle')}</h2>
          <p className="aae-lead">{pageValue(content, defaults, 'valuesLead')}</p>
          <a className="aae-btn aae-btn-primary" style={{ marginTop: 22 }} {...examLinkProps(pageValue(content, defaults, 'valuesCtaUrl'), onNavigate)}>
            {pageValue(content, defaults, 'valuesCtaLabel')}
          </a>
        </div>
        <div className="aae-values-list">
          {visibleItems(pageValue(content, defaults, 'values')).map((value, index) => (
            <article key={`${value.title}-${index}`}>
              <span>{value.number}</span>
              <div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="aae-section" style={{ paddingTop: 0 }}>
      <div className="aae-wrap">
        <div className="aae-experience-panel">
          <div className="aae-experience-mark">A</div>
          <div>
            <div className="aae-kicker" style={{ color: '#d8ff91' }}>{pageValue(content, defaults, 'experienceKicker')}</div>
            <h2>{pageValue(content, defaults, 'experienceTitle')}</h2>
          </div>
          <div className="aae-experience-points">
            {(pageValue(content, defaults, 'experiencePoints') || []).map((point, index) => (
              <span key={`${point}-${index}`}>{point}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  </>
);

const ContactPage = ({ content, defaults, contactRows, workingHours, company, auth, onNavigate }) => {
  const fields = visibleItems(pageValue(content, defaults, 'fields'));
  // Fields are content-driven, so form values and errors are keyed by their stable render index.
  const [fieldValues, setFieldValues] = React.useState({});
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [status, setStatus] = React.useState(null);

  const validateField = (field, value) => {
    const trimmed = String(value || '').trim();
    const isOptional = field.required === false || /optional/i.test(String(field.placeholder || ''));
    if (!trimmed) return isOptional ? '' : `${field.label} is required.`;
    if (inquiryFieldRole(field) === 'email') return getEmailValidationMessage(trimmed);
    if (inquiryFieldRole(field) === 'phoneNumber' && trimmed.replace(/\D/g, '').length < 7) {
      return 'Enter a valid phone number.';
    }
    return '';
  };

  const handleFieldChange = (field, index) => (event) => {
    const { value } = event.target;
    setFieldValues((current) => ({ ...current, [index]: value }));
    setStatus(null);
    if (fieldErrors[index]) {
      setFieldErrors((current) => ({ ...current, [index]: validateField(field, value) }));
    }
  };

  const handleFieldBlur = (field, index) => () => {
    setFieldErrors((current) => ({ ...current, [index]: validateField(field, fieldValues[index]) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const trimmedValues = {};
    const nextErrors = {};

    fields.forEach((field, index) => {
      const trimmed = String(fieldValues[index] || '').trim();
      trimmedValues[index] = trimmed;
      nextErrors[index] = validateField(field, trimmed);
    });

    setFieldValues(trimmedValues);
    setFieldErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) return;

    setSubmitting(true);
    setStatus(null);
    try {
      const result = await createInquiry(buildExamInquiryPayload(fields, trimmedValues, company));
      localStorage.setItem('inquiryData', JSON.stringify(result));
      setFieldValues({});
      setFieldErrors({});
      setStatus({ type: 'success', message: 'Thanks for reaching out. We will be in touch soon.' });
    } catch {
      setStatus({ type: 'error', message: 'Error sending enquiry. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero content={content} defaults={defaults} auth={auth} onNavigate={onNavigate} crumb={pageValue(content, defaults, 'crumb')}>
        <div className="aae-eyebrow">{pageValue(content, defaults, 'eyebrow')}</div>
        <h1>{pageValue(content, defaults, 'title')}</h1>
        <p>{pageValue(content, defaults, 'subtitle')}</p>
      </PageHero>

      <section className="aae-section">
        <div className="aae-wrap aae-contact-grid">
          <form className="aae-form-card" onSubmit={handleSubmit} noValidate>
            <h2 className="aae-title" style={{ fontSize: 30, marginTop: 0 }}>{pageValue(content, defaults, 'formTitle')}</h2>
            <p className="aae-lead" style={{ fontSize: 15 }}>{pageValue(content, defaults, 'formLead')}</p>
            <div className="aae-form-grid">
              {fields.map((field, index) => {
                const fieldId = `aae-field-${index}`;
                const fieldError = fieldErrors[index] || '';

                return (
                  <div className={`aae-field${field.wide || field.type === 'textarea' ? ' is-wide' : ''}`} key={`${field.label}-${index}`}>
                    <label htmlFor={fieldId}>{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        id={fieldId}
                        className={fieldError ? 'is-invalid' : undefined}
                        placeholder={field.placeholder}
                        value={fieldValues[index] || ''}
                        onChange={handleFieldChange(field, index)}
                        onBlur={handleFieldBlur(field, index)}
                        aria-invalid={fieldError ? 'true' : 'false'}
                        aria-describedby={fieldError ? `${fieldId}-error` : undefined}
                      />
                    ) : (
                      <input
                        id={fieldId}
                        type={field.type || 'text'}
                        className={fieldError ? 'is-invalid' : undefined}
                        placeholder={field.placeholder}
                        value={fieldValues[index] || ''}
                        onChange={handleFieldChange(field, index)}
                        onBlur={handleFieldBlur(field, index)}
                        aria-invalid={fieldError ? 'true' : 'false'}
                        aria-describedby={fieldError ? `${fieldId}-error` : undefined}
                      />
                    )}
                    {fieldError ? (
                      <span className="aae-field-error" id={`${fieldId}-error`} role="alert">{fieldError}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {status ? <div className={`aae-alert is-${status.type}`} role="status">{status.message}</div> : null}
            <div className="aae-form-actions">
              <button className="aae-btn aae-btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Sending...' : pageValue(content, defaults, 'submitLabel')}
              </button>
            </div>
          </form>

          <div className="aae-contact-side">
            <div className="aae-contact-card">
              <h3>{pageValue(content, defaults, 'contactTitle')}</h3>
              {visibleItems(contactRows || pageValue(content, defaults, 'contactRows')).map((row, index) => (
                <div className="aae-contact-row" key={`${row.label}-${index}`}>
                  <i>{row.icon}</i>
                  <div>
                    <strong>{row.value}</strong>
                    <span>{row.label}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="aae-hours-card">
              <h3>{pageValue(content, defaults, 'hoursTitle')}</h3>
              {visibleItems(workingHours || pageValue(content, defaults, 'hours')).map((row, index) => (
                <div className="aae-hours-row" key={`${row.label}-${index}`}>
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const InfoPage = ({ content, defaults, imageUrl, auth, onNavigate }) => (
  <>
    <PageHero content={content} defaults={defaults} auth={auth} onNavigate={onNavigate} crumb={pageValue(content, defaults, 'crumb')}>
      <div className="aae-eyebrow">{pageValue(content, defaults, 'eyebrow')}</div>
      <h1>{pageValue(content, defaults, 'title')}</h1>
      <p>{pageValue(content, defaults, 'subtitle')}</p>
    </PageHero>

    <section className="aae-section">
      <div className="aae-wrap aae-info-grid">
        <div className="aae-info-copy">
          <div className="aae-kicker">{pageValue(content, defaults, 'crumb')}</div>
          <h2 className="aae-title">{pageValue(content, defaults, 'bodyTitle')}</h2>
          {(pageValue(content, defaults, 'paragraphs') || []).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
        <div className="aae-info-photo">
          <img src={imageUrl} alt={pageValue(content, defaults, 'imageAlt') || 'Candidate preparing for a certification exam'} />
        </div>
      </div>

      <div className="aae-wrap aae-info-points">
        {visibleItems(pageValue(content, defaults, 'points')).map((point, index) => (
          <div className="aae-info-point" key={`${point.title}-${index}`}>
            <i>✓</i>
            <div>
              <strong>{point.title}</strong>
              <span>{point.description}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  </>
);

/**
 * Frame for the functional pages. Renders the design's nav, page head and footer and hosts
 * the existing live component (cart, order history, auth) as `children`, so behaviour is
 * untouched while the page still reads as part of the Exam Portal.
 */
const ShellPage = ({ content, defaults, children, auth, onNavigate }) => {
  const isAuthShell = ['authSignIn', 'authSignUp'].includes(content?.previewKey)
    || ['Login', 'Signup'].includes(content?.componentKey);

  return (
    <>
      <PageHero compact={isAuthShell} content={content} defaults={defaults} auth={auth} onNavigate={onNavigate} crumb={pageValue(content, defaults, 'crumb')}>
        <div className="aae-eyebrow">{pageValue(content, defaults, 'eyebrow')}</div>
        <h1>{pageValue(content, defaults, 'title')}</h1>
        {pageValue(content, defaults, 'subtitle') ? <p>{pageValue(content, defaults, 'subtitle')}</p> : null}
      </PageHero>

      <section className="aae-shell-body">
        <div className={`aae-shell-panel${isAuthShell ? ` aae-auth-shell-panel aae-auth-shell-${content?.previewKey === 'authSignUp' || content?.componentKey === 'Signup' ? 'sign-up' : 'sign-in'}` : ''}`}>{children}</div>
      </section>
    </>
  );
};

/**
 * Exam Portal inner pages (courses / course detail / about / contact / info / shell).
 *
 * @param {object} props
 * @param {object} props.section the persisted section; `section.type` selects the page
 * @param {object} [props.data] live education data — `{ products, categories, isLoggedIn }`
 * @param {React.ReactNode} [props.children] body for the `educationExamShell` variant
 * @param {object} [props.auth] candidate auth state; drives the nav's account control
 * @param {(course: object) => void} [props.onSelectCourse] invoked when a catalog card is
 *   chosen. The reader wires this to the Default template's own handler so the detail and
 *   purchase flow is entered identically; the Console preview leaves it undefined and the
 *   cards behave as plain links.
 */
export default function EducationExamPagesSection({
  section,
  data,
  children,
  auth,
  onSelectCourse,
  courseDetail,
  onNavigate
}) {
  const type = section?.type || EXAM_PAGES_TYPES.COURSES;
  const authoredContent = section?.content || {};
  const defaults = pageDefaultsFor(type);
  const normalizedCompanyContext = getTemplateCompanyContext(
    data?.company,
    data?.companyDetails,
    data?.companyDetail,
    data?.companyState,
    courseDetail?.company
  );
  const companyContext = Object.keys(normalizedCompanyContext).length ? normalizedCompanyContext : undefined;
  const content = getExamChromeContent(authoredContent, companyContext);
  const companyName = getExamCompanyName(companyContext);
  const contactRows = companyContactRows(
    companyContext,
    pageValue(content, defaults, 'contactRows')
  );
  const workingHours = companyWorkingHoursRows(
    companyContext,
    pageValue(content, defaults, 'hours')
  );
  const imageUrl = content.studentImageUrl || content.imageUrl || content.heroImageUrl || DEFAULT_STUDENT_IMAGE;

  // Real products drive the catalog. No slice: the catalog is the full listing, filtered and
  // sorted in ExamCatalog. Authored content is only a fallback for the builder preview.
  const hasLiveCatalog = Array.isArray(data?.products);
  const liveProducts = visibleItems(data?.products);
  const cards = hasLiveCatalog
    ? liveProducts.map((course, index) => toCatalogCard(course, index))
    : visibleItems(content.courses);

  // Detail page: real course record and curriculum override the designed placeholders. The
  // Console preview passes no courseDetail, so it keeps showing the design's sample blueprint.
  const liveCourse = courseDetail?.course;
  const shouldReturnToCourseCatalog = type === EXAM_PAGES_TYPES.COURSE_DETAIL
    && Boolean(courseDetail)
    && !courseDetail.hasCourseSelection;
  const showCourseDetailStatus = type === EXAM_PAGES_TYPES.COURSE_DETAIL
    && Boolean(courseDetail)
    && !liveCourse;
  const showCourseDetailLoader = showCourseDetailStatus && Boolean(courseDetail?.loading);

  React.useEffect(() => {
    if (shouldReturnToCourseCatalog) onNavigate?.('/course');
  }, [onNavigate, shouldReturnToCourseCatalog]);
  const liveExams = examMetadata(courseDetail?.curriculums);
  const liveModules = curriculumModules(courseDetail?.curriculums);
  const detailModules = liveCourse
    ? (liveModules.length ? liveModules : examBlueprint(liveExams))
    : undefined;
  const assessmentItems = liveCourse ? examAssessmentItems(liveExams) : undefined;
  const summary = liveCourse ? examSummary(liveExams) : undefined;
  const instructor = liveCourse && courseDetail?.selectedSchedule ? scheduleInstructor(courseDetail.selectedSchedule) : null;
  const totalQuestions = liveExams.reduce((sum, exam) => sum + (Number(exam.noOfQuestions ?? exam.questionCount ?? 0) || 0), 0);
  const totalDuration = liveExams.reduce((sum, exam) => sum + examDurationMinutes(exam), 0);
  const detailContent = liveCourse
    ? {
      ...content,
      crumb: liveCourse.name || content.crumb,
      eyebrow: liveCourse.category?.name || content.eyebrow,
      title: liveCourse.name || content.title,
      subtitle: plainText(liveCourse.description, 260) || 'Review the configured assessment details and available schedules below.',
      pills: [
        deliveryLabel(liveCourse),
        courseDetail?.isScheduleRequired ? 'Scheduled' : 'Attempt anytime',
        validityLabel(liveCourse.lifeTimeInDays),
        liveCourse.status === 'UPCOMING' ? 'Upcoming' : 'Available now'
      ].filter(Boolean),
      priceLabel: <ExamPrice course={liveCourse} company={companyContext} />,
      overview: plainText(liveCourse.description, 900) || 'An overview has not been configured for this exam.',
      certificateBody: `Clear the configured pass mark and receive a shareable, verifiable ${companyName || 'academy'} certificate.`,
      curriculumMeta: liveExams.length
        ? `${liveExams.length} ${liveExams.length === 1 ? 'section' : 'sections'} · ${totalQuestions || 0} questions · ${durationLabel(totalDuration)}`
        : 'Exam metadata has not been configured.',
      sideList: [
        { label: 'Category', value: liveCourse.category?.name || '—' },
        { label: 'Delivery', value: deliveryLabel(liveCourse) || '—' },
        { label: 'Sections', value: liveExams.length ? String(liveExams.length) : '—' },
        { label: 'Questions', value: totalQuestions ? String(totalQuestions) : '—' },
        { label: 'Duration', value: durationLabel(totalDuration) },
        { label: 'Validity', value: validityLabel(liveCourse.lifeTimeInDays) },
        { label: 'Price', value: <ExamPrice course={liveCourse} company={companyContext} /> },
        ...(courseDetail?.schedules?.length
          ? [{ label: 'Schedules', value: String(courseDetail.schedules.length) }]
          : [])
      ]
    }
    : content;
  const aboutContent = type === EXAM_PAGES_TYPES.ABOUT
    ? resolveExamAboutContent(content, defaults, {
      company: companyContext,
      products: data?.products,
      categories: data?.categories,
      loading: data?.examCatalogLoading
    })
    : content;

  return (
    <div className="aae-home" data-education-exam-page={type}>
      <style>{EXAM_HOME_CSS}</style>
      <style>{EXAM_PAGES_CSS}</style>

      {type === EXAM_PAGES_TYPES.COURSE_DETAIL && (
        showCourseDetailLoader ? (
          <CourseDetailLoadingPage />
        ) : showCourseDetailStatus ? (
          <CourseDetailStatusPage
            auth={auth}
            loading={Boolean(courseDetail?.loading)}
            redirecting={shouldReturnToCourseCatalog}
            onNavigate={onNavigate}
          />
        ) : (
          <CourseDetailPage
            content={detailContent}
            defaults={defaults}
            auth={auth}
            modules={detailModules}
            assessmentItems={assessmentItems}
            summary={summary}
            instructor={instructor}
            metadataState={{ loading: courseDetail?.curriculumLoading, error: courseDetail?.curriculumError }}
            purchase={courseDetail}
            onNavigate={onNavigate}
          />
        )
      )}
      {type === EXAM_PAGES_TYPES.ABOUT && <AboutPage content={content} defaults={defaults} imageUrl={imageUrl} auth={auth} onNavigate={onNavigate} />}
      {type === EXAM_PAGES_TYPES.CONTACT && <ContactPage content={content} defaults={defaults} contactRows={contactRows} workingHours={workingHours} company={companyContext} auth={auth} onNavigate={onNavigate} />}
      {type === EXAM_PAGES_TYPES.INFO && <InfoPage content={content} defaults={defaults} imageUrl={imageUrl} auth={auth} onNavigate={onNavigate} />}
      {type === EXAM_PAGES_TYPES.SHELL && <ShellPage content={content} defaults={defaults} auth={auth} onNavigate={onNavigate}>{children}</ShellPage>}
      {type === EXAM_PAGES_TYPES.COURSES && (
        <CoursesPage
          content={content}
          defaults={defaults}
          cards={cards}
          auth={auth}
          categories={data?.categories}
          onSelectCourse={onSelectCourse}
          onNavigate={onNavigate}
          companyName={companyName}
          loading={Boolean(data?.examCatalogLoading)}
          error={data?.examCatalogError}
          onRetry={data?.retryExamCatalog}
        />
      )}

      <ExamFooter content={{ ...CHROME_DEFAULTS, ...content }} company={companyContext} onNavigate={onNavigate} />
    </div>
  );
}

